import type {
  AdPerformanceScope,
  FaireAdPerformancePeriodEntity,
} from "@faire/playground-entity-db/entity_types/adPerformancePeriod";

/**
 * Real-brand prototype variants, sourced from Snowflake `ANALYTICS.ADS`.
 *
 * REFRESH BEFORE INTERVIEWS: re-run the queries in `snowflakeQueries.ts`, then
 * replace the `months` / `current` numbers below. The accessors and the toolbar
 * read from here, so those need nothing further.
 *
 * `overview` below is the EXCEPTION and the one place a refresh can silently go
 * wrong. Nothing renders it — every consumer reads `getP8Profile().overview`,
 * which resolves to the advertiser-profile entity file — so it is a parity record
 * only. Update the entity's `overview` to match, or the page will keep showing
 * the old totals while this file looks correct.
 *
 * Source tables (found via the `snowbrain-query` skill's `ads` semantic model):
 *   - ANALYTICS.ADS_SERVER.ADVERTISERS   (brand_token -> advertiser id, budget)
 *   - ANALYTICS.ADS_SERVER.AD_CAMPAIGNS  (advertiser_id -> campaigns)
 *   - ANALYTICS.ADS.CAMPAIGN_PERIODS     (monthly performance, first-year value)
 *
 * MODELING NOTE: `CAMPAIGN_PERIODS` does not attribute first-year GMV to the
 * REORDER segment (those value columns are 0). Reorder value below is MODELED as
 * `reorderConversions * aov`, where `aov` is the brand's new-customer average
 * order value. New-customer figures are 100% real.
 */

type SegmentMonth = {
  spend: number;
  /** best-estimate first-year value */
  firstYearValue: number;
  /**
   * Immediate first-order ad sales for the segment (real brands only). When set,
   * the segment uses real per-month sales instead of the modeled reorder curve.
   * For the reorder segment this equals its attributed reorder ad sales.
   *
   * Source is CAMPAIGN_PERIODS.TOTAL_LOCAL_POST_DISCOUNT — the sales attributed
   * directly to ad clicks. Do NOT use ..._FROM_ADS_ACQUIRED_CUSTOMERS_EX_ADS_REORDERS:
   * despite the name it is not "first orders", it equals FROM_ADS_ACQUIRED_CUSTOMERS
   * to the cent on every brand in this file, so pairing the two below silently
   * reports zero reorder sales. Dairy Brand was built that way at first
   * and showed a flat zero continued band across its whole visible window.
   */
  firstOrderSales?: number;
  /**
   * Actual sales-to-date incl. continued reorders (real brands only), from
   * CAMPAIGN_PERIODS.TOTAL_LOCAL_POST_DISCOUNT_FROM_ADS_ACQUIRED_CUSTOMERS.
   *
   * Reorder sales is the gap between this and `firstOrderSales`, so the two
   * must come from different columns. Note this is sales landing in the month
   * from the whole ads-acquired base, which buckets continued sales by order
   * month rather than by the acquisition month the row is labelled with.
   */
  salesToDate?: number;
  conversions: number;
  clicks: number;
  impressions: number;
  /** prorated monthly budget in effect */
  budget: number;
};

type RealBrandMonth = {
  /** ISO month start, e.g. "2025-07" */
  month: string;
  new: SegmentMonth;
  reorder?: SegmentMonth;
  /**
   * Shared monthly budget shown in the Budget column (new + reorder draw from one
   * pool). When set, it overrides the sum of the segment budgets for the overview.
   */
  pooledBudget?: number;
  /** Projected month-end utilization for the in-progress month (0–1 decimal). */
  projectedUtilization?: number;
};

/**
 * Lifetime program totals, across the advertiser's whole tenure rather than the
 * twelve months `months` carries.
 *
 * The sales and the ROAS fields come from different columns and must not be
 * derived from each other. Ad sales is money already booked
 * (TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS for new, TOTAL_SALES for
 * reorder); first-year ROAS is measured against a projection
 * (BEST_ESTIMATE_1YR_TOTAL_SALES...). The projection can sit either side of
 * booked sales — above it for a young cohort still expected to reorder, below it
 * for an older one that has outrun its first year — so multiplying a ROAS by
 * spend does not reconstruct ad sales.
 */
type RealBrandOverview = {
  numCustomersAcquired: number;
  numReorderConversions: number;
  numTotalConversions: number;
  /** Realized sales to date, NOT the first-year projection. */
  totalAdsSalesDollars: number;
  /** TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS, summed over all periods. */
  newCustomerAdsSalesDollars: number;
  /** Reorder TOTAL_SALES_LOCAL_CENTS; the acquired-customer columns read 0 here. */
  reorderAdsSalesDollars: number;
  totalAdsSpendDollars: number;
  newCustomerAdsSpendDollars: number;
  reorderAdsSpendDollars: number;
  /** BEST_ESTIMATE_1YR_TOTAL_SALES... over new-customer spend. */
  newCustomerBestEstimateFirstYearRoas: number;
  reorderRoas: number;
  combinedFirstYearRoas: number;
};

type RealBrandConfig = {
  profileToken: string;
  tokenPrefix: string;
  /** new-customer average order value, used to model reorder GMV */
  aov: number;
  /** label for the right edge of the lifetime window, e.g. "Jul 10, 2026" */
  anchorLabel: string;
  overview: RealBrandOverview;
  /** complete months, chronological (oldest first) — the last 12 shown in the chart */
  months: RealBrandMonth[];
  /** partial in-progress month (omit for paused advertisers) */
  current?: RealBrandMonth;
};

const TOTALLY_BAMBOO: RealBrandConfig = {
  profileToken: "adv_totally_bamboo",
  tokenPrefix: "tb",
  aov: 299,
  anchorLabel: "Jul 10, 2026",
  overview: {
    numCustomersAcquired: 1078,
    numReorderConversions: 471,
    numTotalConversions: 1549,
    totalAdsSalesDollars: 747660,
    newCustomerAdsSalesDollars: 606831,
    reorderAdsSalesDollars: 140829,
    totalAdsSpendDollars: 109342,
    newCustomerAdsSpendDollars: 95503,
    reorderAdsSpendDollars: 13839,
    newCustomerBestEstimateFirstYearRoas: 6.35,
    reorderRoas: 10.18,
    combinedFirstYearRoas: 6.84,
  },
  months: [
    {
      month: "2025-07",
      new: {
        spend: 2724,
        firstYearValue: 10558,
        conversions: 29,
        clicks: 613,
        impressions: 39107,
        budget: 6688,
      },
      reorder: {
        spend: 927,
        firstYearValue: 12857,
        conversions: 43,
        clicks: 152,
        impressions: 9948,
        budget: 5992,
      },
    },
    {
      month: "2025-08",
      new: {
        spend: 4855,
        firstYearValue: 18199,
        conversions: 45,
        clicks: 951,
        impressions: 54997,
        budget: 8000,
      },
      reorder: {
        spend: 423,
        firstYearValue: 8073,
        conversions: 27,
        clicks: 80,
        impressions: 6224,
        budget: 8000,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 7374,
        firstYearValue: 33931,
        conversions: 64,
        clicks: 1118,
        impressions: 73916,
        budget: 7467,
      },
      reorder: {
        spend: 434,
        firstYearValue: 6877,
        conversions: 23,
        clicks: 83,
        impressions: 6141,
        budget: 6400,
      },
    },
    {
      month: "2025-10",
      new: {
        spend: 6642,
        firstYearValue: 39653,
        conversions: 71,
        clicks: 883,
        impressions: 58915,
        budget: 7065,
      },
      reorder: {
        spend: 711,
        firstYearValue: 6578,
        conversions: 22,
        clicks: 120,
        impressions: 6595,
        budget: 2000,
      },
    },
    {
      month: "2025-11",
      new: {
        spend: 5041,
        firstYearValue: 21012,
        conversions: 47,
        clicks: 953,
        impressions: 64184,
        budget: 11000,
      },
      reorder: {
        spend: 775,
        firstYearValue: 8073,
        conversions: 27,
        clicks: 100,
        impressions: 6543,
        budget: 2000,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 4050,
        firstYearValue: 15643,
        conversions: 36,
        clicks: 804,
        impressions: 50461,
        budget: 11000,
      },
      reorder: {
        spend: 782,
        firstYearValue: 6279,
        conversions: 21,
        clicks: 115,
        impressions: 7523,
        budget: 2000,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 7482,
        firstYearValue: 30934,
        conversions: 66,
        clicks: 1121,
        impressions: 79094,
        budget: 12032,
      },
      reorder: {
        spend: 1667,
        firstYearValue: 17342,
        conversions: 58,
        clicks: 209,
        impressions: 14994,
        budget: 2516,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 4532,
        firstYearValue: 23789,
        conversions: 52,
        clicks: 694,
        impressions: 49861,
        budget: 13000,
      },
      reorder: {
        spend: 522,
        firstYearValue: 7475,
        conversions: 25,
        clicks: 103,
        impressions: 10586,
        budget: 3000,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 3913,
        firstYearValue: 18220,
        conversions: 37,
        clicks: 904,
        impressions: 61583,
        budget: 13000,
      },
      reorder: {
        spend: 837,
        firstYearValue: 7475,
        conversions: 25,
        clicks: 134,
        impressions: 12025,
        budget: 3000,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 7665,
        firstYearValue: 43853,
        conversions: 77,
        clicks: 993,
        impressions: 69367,
        budget: 13000,
      },
      reorder: {
        spend: 1773,
        firstYearValue: 11063,
        conversions: 37,
        clicks: 173,
        impressions: 14243,
        budget: 3000,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 3665,
        firstYearValue: 21027,
        conversions: 38,
        clicks: 478,
        impressions: 32470,
        budget: 5097,
      },
      reorder: {
        spend: 1252,
        firstYearValue: 10166,
        conversions: 34,
        clicks: 133,
        impressions: 11371,
        budget: 3000,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 7511,
        firstYearValue: 27868,
        conversions: 59,
        clicks: 788,
        impressions: 52659,
        budget: 10000,
      },
      reorder: {
        spend: 1431,
        firstYearValue: 11063,
        conversions: 37,
        clicks: 127,
        impressions: 11123,
        budget: 3000,
      },
    },
  ],
  current: {
    month: "2026-07",
    new: {
      spend: 3792,
      firstYearValue: 14851,
      conversions: 29,
      clicks: 456,
      impressions: 28724,
      budget: 5484,
    },
    reorder: {
      spend: 299,
      firstYearValue: 1794,
      conversions: 6,
      clicks: 84,
      impressions: 5948,
      budget: 1645,
    },
  },
};

const PENGUIN_RANDOM_HOUSE: RealBrandConfig = {
  profileToken: "adv_penguin_rh",
  tokenPrefix: "prh",
  aov: 249,
  anchorLabel: "Oct 14, 2025",
  overview: {
    numCustomersAcquired: 130,
    numReorderConversions: 0,
    numTotalConversions: 130,
    totalAdsSalesDollars: 72258,
    newCustomerAdsSalesDollars: 72258,
    reorderAdsSalesDollars: 0,
    totalAdsSpendDollars: 5639,
    newCustomerAdsSpendDollars: 5639,
    reorderAdsSpendDollars: 0,
    newCustomerBestEstimateFirstYearRoas: 12.81,
    reorderRoas: 0,
    combinedFirstYearRoas: 12.81,
  },
  months: [
    {
      month: "2024-10",
      new: {
        spend: 242,
        firstYearValue: 4540,
        conversions: 11,
        clicks: 82,
        impressions: 3234,
        budget: 242,
      },
    },
    {
      month: "2024-11",
      new: {
        spend: 500,
        firstYearValue: 8174,
        conversions: 22,
        clicks: 167,
        impressions: 7187,
        budget: 500,
      },
    },
    {
      month: "2024-12",
      new: {
        spend: 500,
        firstYearValue: 2885,
        conversions: 7,
        clicks: 169,
        impressions: 5384,
        budget: 500,
      },
    },
    {
      month: "2025-01",
      new: {
        spend: 500,
        firstYearValue: 3312,
        conversions: 10,
        clicks: 167,
        impressions: 5674,
        budget: 500,
      },
    },
    {
      month: "2025-02",
      new: {
        spend: 500,
        firstYearValue: 3268,
        conversions: 8,
        clicks: 174,
        impressions: 7381,
        budget: 500,
      },
    },
    {
      month: "2025-03",
      new: {
        spend: 500,
        firstYearValue: 4055,
        conversions: 14,
        clicks: 173,
        impressions: 7907,
        budget: 500,
      },
    },
    {
      month: "2025-04",
      new: {
        spend: 500,
        firstYearValue: 14234,
        conversions: 19,
        clicks: 174,
        impressions: 6121,
        budget: 500,
      },
    },
    {
      month: "2025-05",
      new: {
        spend: 398,
        firstYearValue: 1640,
        conversions: 4,
        clicks: 160,
        impressions: 6043,
        budget: 500,
      },
    },
    {
      month: "2025-06",
      new: {
        spend: 500,
        firstYearValue: 9910,
        conversions: 12,
        clicks: 154,
        impressions: 6956,
        budget: 500,
      },
    },
    {
      month: "2025-07",
      new: {
        spend: 500,
        firstYearValue: 4666,
        conversions: 9,
        clicks: 137,
        impressions: 5798,
        budget: 500,
      },
    },
    {
      month: "2025-08",
      new: {
        spend: 500,
        firstYearValue: 4147,
        conversions: 5,
        clicks: 106,
        impressions: 4431,
        budget: 500,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 500,
        firstYearValue: 11428,
        conversions: 9,
        clicks: 98,
        impressions: 3397,
        budget: 500,
      },
    },
  ],
};

/**
 * The Good Book Company (b_8zv2s38j7b, advertiser -7626954770814929455).
 * Real numbers (complete months Nov 2025 – Jul 2026; ~9-month tenure, small
 * pooled budget). Modest-but-successful archetype.
 *
 * Sales are 100% real per the platform. Per-segment monthly figures:
 *   - NEW: first-order sales from CAMPAIGN_PERIODS.TOTAL_LOCAL_POST_DISCOUNT and
 *     sales-to-date from ...FROM_ADS_ACQUIRED_CUSTOMERS, first-year value from
 *     BEST_ESTIMATE_1YR_TOTAL_LOCAL_POST_DISCOUNT...
 *     (This line used to name TOTAL_LOCAL_POST_DISCOUNT..._EX_ADS_REORDERS as the
 *     first-order column. It isn't: that column tracks FROM_ADS_ACQUIRED_CUSTOMERS
 *     exactly on every brand here, so using it zeroes out continued sales. See the
 *     note on SegmentMonth.firstOrderSales.)
 *   - REORDER: CAMPAIGN_PERIODS reports 0 GMV for reorder, so reorder ad sales come
 *     from ADS.BRAND_ORDERS (POST_DISCOUNT_TOTAL by order month, reorder campaign).
 *     Reorder GMV has no continuation, so its first-order = sales-to-date = first-year.
 * Combined per month = NEW + REORDER, matching the platform's Ads Manager exactly.
 */
const GOOD_BOOK_COMPANY: RealBrandConfig = {
  profileToken: "adv_good_book_company",
  tokenPrefix: "gbc",
  aov: 274,
  anchorLabel: "Aug 3, 2026",
  overview: {
    numCustomersAcquired: 29,
    numReorderConversions: 3,
    numTotalConversions: 32,
    totalAdsSalesDollars: 15078,
    newCustomerAdsSalesDollars: 14496,
    reorderAdsSalesDollars: 582,
    totalAdsSpendDollars: 2427,
    newCustomerAdsSpendDollars: 2296,
    reorderAdsSpendDollars: 131,
    newCustomerBestEstimateFirstYearRoas: 6.31,
    reorderRoas: 4.2,
    combinedFirstYearRoas: 6.2,
  },
  months: [
    {
      month: "2025-11",
      pooledBudget: 288,
      new: {
        spend: 203.65,
        firstOrderSales: 670.82,
        salesToDate: 670.82,
        firstYearValue: 831.71,
        conversions: 2,
        clicks: 40,
        impressions: 2653,
        budget: 500,
      },
      reorder: {
        spend: 10.19,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 124,
        budget: 150,
      },
    },
    {
      month: "2025-12",
      pooledBudget: 320,
      new: {
        spend: 208.56,
        firstOrderSales: 417.49,
        salesToDate: 529.57,
        firstYearValue: 710.37,
        conversions: 2,
        clicks: 91,
        impressions: 5257,
        budget: 500,
      },
      reorder: {
        spend: 13.47,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 8,
        impressions: 330,
        budget: 150,
      },
    },
    {
      month: "2026-01",
      pooledBudget: 320,
      new: {
        spend: 290.72,
        firstOrderSales: 2705.24,
        salesToDate: 2705.24,
        firstYearValue: 3827.28,
        conversions: 7,
        clicks: 83,
        impressions: 5442,
        budget: 500,
      },
      reorder: {
        spend: 7.86,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 210,
        budget: 150,
      },
    },
    {
      month: "2026-02",
      pooledBudget: 320,
      new: {
        spend: 258.66,
        firstOrderSales: 1054.45,
        salesToDate: 1054.45,
        firstYearValue: 1887.86,
        conversions: 5,
        clicks: 83,
        impressions: 5513,
        budget: 500,
      },
      reorder: {
        spend: 7.61,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 244,
        budget: 150,
      },
    },
    {
      month: "2026-03",
      pooledBudget: 320,
      new: {
        spend: 268.24,
        firstOrderSales: 1977.96,
        salesToDate: 2749.08,
        firstYearValue: 4278.1,
        conversions: 7,
        clicks: 52,
        impressions: 3614,
        budget: 500,
      },
      reorder: {
        spend: 13.42,
        firstOrderSales: 455.9,
        salesToDate: 455.9,
        firstYearValue: 455.9,
        conversions: 2,
        clicks: 4,
        impressions: 126,
        budget: 150,
      },
    },
    {
      month: "2026-04",
      pooledBudget: 464,
      new: {
        spend: 130.78,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 58,
        impressions: 3747,
        budget: 500,
      },
      reorder: {
        spend: 21.7,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 5,
        impressions: 275,
        budget: 150,
      },
    },
    {
      month: "2026-05",
      pooledBudget: 500,
      new: {
        spend: 170.18,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 47,
        impressions: 3401,
        budget: 500,
      },
      reorder: {
        spend: 12.77,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 204,
        budget: 150,
      },
    },
    {
      month: "2026-06",
      pooledBudget: 500,
      new: {
        spend: 306.02,
        firstOrderSales: 262.02,
        salesToDate: 262.02,
        firstYearValue: 776.43,
        conversions: 2,
        clicks: 51,
        impressions: 3176,
        budget: 500,
      },
      reorder: {
        spend: 20.5,
        firstOrderSales: 126.31,
        salesToDate: 126.31,
        firstYearValue: 126.31,
        conversions: 1,
        clicks: 6,
        impressions: 280,
        budget: 150,
      },
    },
    {
      month: "2026-07",
      pooledBudget: 500,
      new: {
        spend: 458.48,
        firstOrderSales: 864.03,
        salesToDate: 864.03,
        firstYearValue: 2185.04,
        conversions: 4,
        clicks: 66,
        impressions: 3930,
        budget: 500,
      },
      reorder: {
        spend: 22.73,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 5,
        impressions: 304,
        budget: 150,
      },
    },
  ],
  current: {
    month: "2026-08",
    pooledBudget: 500,
    projectedUtilization: 0.837,
    new: {
      spend: 28.64,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 1,
      impressions: 262,
      budget: 500,
    },
    reorder: {
      spend: 9.57,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 1,
      impressions: 21,
      budget: 150,
    },
  },
};

/**
 * MALWEST design (b_cm6qg8te3j, advertiser -5243943702272902340).
 * Real numbers (complete months Jan 2025 – Jul 2026; ~19-month tenure). NEW_ONLY
 * — a single new-customers campaign, still RUNNING, with a budget that scaled up
 * over 2026 (raised to $1,800/mo in Aug). Growing-but-early archetype: modest
 * spend, lumpy acquisition (several zero-conversion months), ~2.3x first-year ROAS.
 *
 * Sales are 100% real per the platform, straight from
 * MANAGER_METRICS_CAMPAIGN_PERIODS:
 *   - firstOrderSales = TOTAL_SALES_LOCAL_CENTS (immediate first-order sales)
 *   - salesToDate     = TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS
 *   - firstYearValue  = BEST_ESTIMATE_1YR_TOTAL_SALES..._FROM_ADS_ACQUIRED_CUSTOMERS
 * No reorder campaign exists, so every reorder figure is 0. `budget` is the
 * campaign's set monthly budget in effect that month (LAST_SET_BUDGET_LOCAL).
 */
const MALWEST: RealBrandConfig = {
  profileToken: "adv_malwest",
  tokenPrefix: "mw",
  aov: 330,
  anchorLabel: "Aug 10, 2026",
  overview: {
    numCustomersAcquired: 19,
    numReorderConversions: 0,
    numTotalConversions: 19,
    totalAdsSalesDollars: 6564,
    newCustomerAdsSalesDollars: 6564,
    reorderAdsSalesDollars: 0,
    totalAdsSpendDollars: 2934,
    newCustomerAdsSpendDollars: 2934,
    reorderAdsSpendDollars: 0,
    newCustomerBestEstimateFirstYearRoas: 2.28,
    reorderRoas: 0,
    combinedFirstYearRoas: 2.28,
  },
  months: [
    {
      month: "2025-01",
      new: {
        spend: 32.59,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 13,
        impressions: 902,
        budget: 200,
      },
    },
    {
      month: "2025-02",
      new: {
        spend: 56.01,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 22,
        impressions: 1115,
        budget: 200,
      },
    },
    {
      month: "2025-03",
      new: {
        spend: 68.02,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 27,
        impressions: 1108,
        budget: 200,
      },
    },
    {
      month: "2025-04",
      new: {
        spend: 43.22,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 19,
        impressions: 910,
        budget: 200,
      },
    },
    {
      month: "2025-05",
      new: {
        spend: 44.6,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 17,
        impressions: 857,
        budget: 200,
      },
    },
    {
      month: "2025-06",
      new: {
        spend: 95.37,
        firstOrderSales: 370,
        salesToDate: 370,
        firstYearValue: 370,
        conversions: 1,
        clicks: 39,
        impressions: 1290,
        budget: 200,
      },
    },
    {
      month: "2025-07",
      new: {
        spend: 98.96,
        firstOrderSales: 318.6,
        salesToDate: 318.6,
        firstYearValue: 318.6,
        conversions: 1,
        clicks: 78,
        impressions: 1875,
        budget: 200,
      },
    },
    {
      month: "2025-08",
      new: {
        spend: 83.95,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 84,
        impressions: 2094,
        budget: 200,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 106.97,
        firstOrderSales: 336,
        salesToDate: 336,
        firstYearValue: 336,
        conversions: 1,
        clicks: 91,
        impressions: 3924,
        budget: 200,
      },
    },
    {
      month: "2025-10",
      new: {
        spend: 200,
        firstOrderSales: 725.5,
        salesToDate: 725.5,
        firstYearValue: 776.69,
        conversions: 3,
        clicks: 76,
        impressions: 3770,
        budget: 200,
      },
    },
    {
      month: "2025-11",
      new: {
        spend: 114.87,
        firstOrderSales: 216,
        salesToDate: 216,
        firstYearValue: 218.88,
        conversions: 1,
        clicks: 60,
        impressions: 3075,
        budget: 500,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 123.87,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 70,
        impressions: 4454,
        budget: 500,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 123.84,
        firstOrderSales: 203.75,
        salesToDate: 203.75,
        firstYearValue: 213.14,
        conversions: 1,
        clicks: 124,
        impressions: 8260,
        budget: 500,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 92.19,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 61,
        impressions: 3780,
        budget: 500,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 119.54,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 55,
        impressions: 4045,
        budget: 500,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 148.15,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 72,
        impressions: 4983,
        budget: 500,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 394.68,
        firstOrderSales: 635,
        salesToDate: 923.8,
        firstYearValue: 990.31,
        conversions: 3,
        clicks: 102,
        impressions: 4856,
        budget: 500,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 245.18,
        firstOrderSales: 346,
        salesToDate: 346,
        firstYearValue: 346,
        conversions: 1,
        clicks: 106,
        impressions: 5193,
        budget: 500,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 741.93,
        firstOrderSales: 3124.7,
        salesToDate: 3124.7,
        firstYearValue: 3124.7,
        conversions: 7,
        clicks: 151,
        impressions: 7137,
        budget: 800,
      },
    },
  ],
  current: {
    month: "2026-08",
    projectedUtilization: 0.1,
    new: {
      spend: 49.73,
      firstOrderSales: 174,
      salesToDate: 174,
      firstYearValue: 216.59,
      conversions: 1,
      clicks: 37,
      impressions: 2393,
      budget: 1800,
    },
  },
};

/**
 * King Floyd's (b_2ytsec10es, advertiser 6775715154222277513).
 * Real numbers (complete months Jul 2025 – Jul 2026; 13-month tenure).
 * NEW_AND_RETURNING with separate campaign budgets ($800 new + $200 reorder),
 * both RUNNING. Under-utilized archetype: ~$200/mo of a $1,000 budget actually
 * spends, lumpy acquisition (three zero-conversion months), 3.6x combined
 * first-year ROAS with reorders returning ~7x.
 *
 * Sales are 100% real per the platform, from
 * MANAGER_METRICS_CAMPAIGN_PERIODS:
 *   - firstOrderSales = TOTAL_SALES_LOCAL_CENTS (immediate first-order sales)
 *   - salesToDate     = TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS
 *   - firstYearValue  = BEST_ESTIMATE_1YR_TOTAL_SALES..._FROM_ADS_ACQUIRED_CUSTOMERS
 * The reorder campaign reports 0 in the two acquired-customer columns (those are
 * new-customer LTV metrics), so its reorder ad sales come from TOTAL_SALES and,
 * having no continuation, first-order = sales-to-date = first-year.
 * `budget` is the prorated monthly budget in effect that month.
 */
const KING_FLOYDS: RealBrandConfig = {
  profileToken: "adv_king_floyds",
  tokenPrefix: "kf",
  aov: 231,
  anchorLabel: "Aug 18, 2026",
  overview: {
    numCustomersAcquired: 18,
    numReorderConversions: 6,
    numTotalConversions: 24,
    totalAdsSalesDollars: 9522,
    newCustomerAdsSalesDollars: 7718,
    reorderAdsSalesDollars: 1804,
    totalAdsSpendDollars: 2630,
    newCustomerAdsSpendDollars: 2372,
    reorderAdsSpendDollars: 258,
    newCustomerBestEstimateFirstYearRoas: 3.25,
    reorderRoas: 6.99,
    combinedFirstYearRoas: 3.62,
  },
  months: [
    {
      month: "2025-07",
      new: {
        spend: 72.99,
        firstOrderSales: 358.85,
        salesToDate: 358.85,
        firstYearValue: 358.85,
        conversions: 1,
        clicks: 16,
        impressions: 437,
        budget: 335,
      },
      reorder: {
        spend: 6.02,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 1,
        impressions: 171,
        budget: 84,
      },
    },
    {
      month: "2025-08",
      new: {
        spend: 155.47,
        firstOrderSales: 620.08,
        salesToDate: 792.64,
        firstYearValue: 818.27,
        conversions: 3,
        clicks: 35,
        impressions: 1453,
        budget: 800,
      },
      reorder: {
        spend: 6.7,
        firstOrderSales: 496.38,
        salesToDate: 496.38,
        firstYearValue: 496.38,
        conversions: 1,
        clicks: 1,
        impressions: 194,
        budget: 200,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 286.94,
        firstOrderSales: 596.71,
        salesToDate: 868.37,
        firstYearValue: 953.77,
        conversions: 3,
        clicks: 37,
        impressions: 1930,
        budget: 800,
      },
      reorder: {
        spend: 17.88,
        firstOrderSales: 359.76,
        salesToDate: 359.76,
        firstYearValue: 359.76,
        conversions: 1,
        clicks: 2,
        impressions: 204,
        budget: 200,
      },
    },
    {
      month: "2025-10",
      new: {
        spend: 261.12,
        firstOrderSales: 301.98,
        salesToDate: 301.98,
        firstYearValue: 353.7,
        conversions: 1,
        clicks: 31,
        impressions: 2285,
        budget: 800,
      },
      reorder: {
        spend: 12.17,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 234,
        budget: 200,
      },
    },
    {
      month: "2025-11",
      new: {
        spend: 170.52,
        firstOrderSales: 114.96,
        salesToDate: 114.96,
        firstYearValue: 182.21,
        conversions: 1,
        clicks: 34,
        impressions: 1911,
        budget: 800,
      },
      reorder: {
        spend: 39.92,
        firstOrderSales: 395.13,
        salesToDate: 395.13,
        firstYearValue: 395.13,
        conversions: 1,
        clicks: 3,
        impressions: 252,
        budget: 200,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 155.64,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 22,
        impressions: 1557,
        budget: 800,
      },
      reorder: {
        spend: 16.81,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 277,
        budget: 200,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 133.5,
        firstOrderSales: 103.46,
        salesToDate: 103.46,
        firstYearValue: 216.4,
        conversions: 1,
        clicks: 23,
        impressions: 1738,
        budget: 800,
      },
      reorder: {
        spend: 5.55,
        firstOrderSales: 101.27,
        salesToDate: 101.27,
        firstYearValue: 101.27,
        conversions: 1,
        clicks: 2,
        impressions: 344,
        budget: 200,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 60.12,
        firstOrderSales: 509.28,
        salesToDate: 775.44,
        firstYearValue: 1006.21,
        conversions: 1,
        clicks: 9,
        impressions: 720,
        budget: 800,
      },
      reorder: {
        spend: 14.47,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 137,
        budget: 200,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 124.77,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 19,
        impressions: 1040,
        budget: 800,
      },
      reorder: {
        spend: 5.06,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 1,
        impressions: 153,
        budget: 200,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 166.32,
        firstOrderSales: 172.56,
        salesToDate: 697.56,
        firstYearValue: 881.74,
        conversions: 1,
        clicks: 20,
        impressions: 1083,
        budget: 800,
      },
      reorder: {
        spend: 25.39,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 146,
        budget: 200,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 239.87,
        firstOrderSales: 518.46,
        salesToDate: 867.24,
        firstYearValue: 1309.02,
        conversions: 2,
        clicks: 22,
        impressions: 1358,
        budget: 800,
      },
      reorder: {
        spend: 13.25,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 2,
        impressions: 212,
        budget: 200,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 224.7,
        firstOrderSales: 322.62,
        salesToDate: 322.62,
        firstYearValue: 798.64,
        conversions: 2,
        clicks: 16,
        impressions: 771,
        budget: 800,
      },
      reorder: {
        spend: 35.44,
        firstOrderSales: 123.75,
        salesToDate: 123.75,
        firstYearValue: 123.75,
        conversions: 1,
        clicks: 4,
        impressions: 153,
        budget: 200,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 320.24,
        firstOrderSales: 532.57,
        salesToDate: 532.57,
        firstYearValue: 838.96,
        conversions: 2,
        clicks: 18,
        impressions: 1136,
        budget: 800,
      },
      reorder: {
        spend: 59.62,
        firstOrderSales: 327.86,
        salesToDate: 327.86,
        firstYearValue: 327.86,
        conversions: 1,
        clicks: 4,
        impressions: 324,
        budget: 200,
      },
    },
  ],
  current: {
    month: "2026-08",
    projectedUtilization: 0.05,
    new: {
      spend: 30.59,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 8,
      impressions: 448,
      budget: 800,
    },
    reorder: {
      spend: 0,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 0,
      impressions: 94,
      budget: 200,
    },
  },
};

/**
 * Oggi (b_chwjy424g8, advertiser 4748191580351722375).
 * Real numbers (complete months Nov 2025 – Jul 2026; 9-month tenure). NEW_ONLY
 * — a single new-customers campaign, still RUNNING. Budget-capped archetype:
 * spend lands on the monthly budget nearly every month as the budget was raised
 * from ~$85 to $525, 6.6x first-year ROAS, and the platform is recommending an
 * increase. No reorder campaign exists, so every reorder figure is 0.
 *
 * Sales are 100% real per the platform, from
 * MANAGER_METRICS_CAMPAIGN_PERIODS:
 *   - firstOrderSales = TOTAL_SALES_LOCAL_CENTS (immediate first-order sales)
 *   - salesToDate     = TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS
 *   - firstYearValue  = BEST_ESTIMATE_1YR_TOTAL_SALES..._FROM_ADS_ACQUIRED_CUSTOMERS
 * `budget` is the prorated monthly budget in effect that month.
 */
const OGGI: RealBrandConfig = {
  profileToken: "adv_oggi",
  tokenPrefix: "og",
  aov: 315,
  anchorLabel: "Aug 18, 2026",
  overview: {
    numCustomersAcquired: 38,
    numReorderConversions: 0,
    numTotalConversions: 38,
    totalAdsSalesDollars: 18938,
    newCustomerAdsSalesDollars: 18938,
    reorderAdsSalesDollars: 0,
    totalAdsSpendDollars: 2864,
    newCustomerAdsSpendDollars: 2864,
    reorderAdsSpendDollars: 0,
    newCustomerBestEstimateFirstYearRoas: 6.61,
    reorderRoas: 0,
    combinedFirstYearRoas: 6.61,
  },
  months: [
    {
      month: "2025-11",
      new: {
        spend: 83.33,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 13,
        impressions: 1269,
        budget: 83,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 189.63,
        firstOrderSales: 836.55,
        salesToDate: 1307.03,
        firstYearValue: 1530.84,
        conversions: 4,
        clicks: 36,
        impressions: 1896,
        budget: 194,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 230.71,
        firstOrderSales: 1765.03,
        salesToDate: 4197.96,
        firstYearValue: 4436.04,
        conversions: 4,
        clicks: 47,
        impressions: 3418,
        budget: 234,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 348.21,
        firstOrderSales: 977.9,
        salesToDate: 1307.83,
        firstYearValue: 1572.12,
        conversions: 4,
        clicks: 79,
        impressions: 4303,
        budget: 348,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 375,
        firstOrderSales: 1056.7,
        salesToDate: 1056.7,
        firstYearValue: 1240.61,
        conversions: 3,
        clicks: 80,
        impressions: 4033,
        budget: 375,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 409,
        firstOrderSales: 1640.01,
        salesToDate: 1794.01,
        firstYearValue: 2343.72,
        conversions: 6,
        clicks: 72,
        impressions: 4251,
        budget: 428,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 475,
        firstOrderSales: 2183.95,
        salesToDate: 2711.95,
        firstYearValue: 3373.25,
        conversions: 8,
        clicks: 80,
        impressions: 4455,
        budget: 475,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 227.75,
        firstOrderSales: 161.7,
        salesToDate: 666.6,
        firstYearValue: 750.15,
        conversions: 1,
        clicks: 71,
        impressions: 4509,
        budget: 518,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 525,
        firstOrderSales: 3352.72,
        salesToDate: 3352.72,
        firstYearValue: 3691.16,
        conversions: 8,
        clicks: 66,
        impressions: 5052,
        budget: 525,
      },
    },
  ],
  current: {
    month: "2026-08",
    projectedUtilization: 0.66,
    new: {
      spend: 200.26,
      firstOrderSales: 330.55,
      salesToDate: 330.55,
      firstYearValue: 540.11,
      conversions: 2,
      clicks: 41,
      impressions: 3917,
      budget: 525,
    },
  },
};

/**
 * Elana Gabrielle (b_16o7r3v1y0, advertiser 9095933244058406210).
 * Real numbers (complete months Sep 2025 – Aug 2026, of a 31-month tenure that
 * started Mar 2024). NEW_AND_RETURNING, both campaigns RUNNING. Steady-spender
 * archetype: a printed-textile brand advertising at ~$200/mo for two and a half
 * years, 5.0x new-customer first-year ROAS, and the strongest reorder engine of
 * the three — 26 reorder conversions returning ~8.9x, with reorder spend
 * climbing sharply from Jul 2026.
 *
 * Sales are 100% real per the platform, from
 * MANAGER_METRICS_CAMPAIGN_PERIODS:
 *   - firstOrderSales = TOTAL_SALES_LOCAL_CENTS (immediate first-order sales)
 *   - salesToDate     = TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS
 *   - firstYearValue  = BEST_ESTIMATE_1YR_TOTAL_SALES..._FROM_ADS_ACQUIRED_CUSTOMERS
 * The reorder campaign reports 0 in the two acquired-customer columns (those are
 * new-customer LTV metrics), so its reorder ad sales come from TOTAL_SALES and,
 * having no continuation, first-order = sales-to-date = first-year.
 *
 * The budget is POOLED: ADVERTISERS reports one $400 pool serving both
 * campaigns, so `pooledBudget` is set on every month and the per-segment
 * `budget` values are a spend-apportioned split that nothing currently reads.
 * PRORATED_BUDGET_LOCAL_CENTS stops being populated for this advertiser after
 * Jun 2025 and MONTHLY_BUDGET is null throughout, so every month shown reads
 * against the same reported pool rather than a month-specific figure.
 */
const ELANA_GABRIELLE: RealBrandConfig = {
  profileToken: "adv_elana_gabrielle",
  tokenPrefix: "eg",
  // $24,981 of uncapped lifetime new-customer sales over 104 ad-attributed
  // orders. That lifetime figure backs `aov` only, not the totals below.
  aov: 240,
  // Snapshot ds 2026-09-17.
  anchorLabel: "Sep 17, 2026",
  overview: {
    numCustomersAcquired: 51,
    numReorderConversions: 26,
    numTotalConversions: 77,
    totalAdsSalesDollars: 32045,
    newCustomerAdsSalesDollars: 24438,
    reorderAdsSalesDollars: 7607,
    totalAdsSpendDollars: 5211,
    newCustomerAdsSpendDollars: 4353,
    reorderAdsSpendDollars: 858,
    newCustomerBestEstimateFirstYearRoas: 5.03,
    reorderRoas: 8.86,
    combinedFirstYearRoas: 5.66,
  },
  months: [
    {
      month: "2025-09",
      pooledBudget: 400,
      new: {
        spend: 301.61,
        firstOrderSales: 625.75,
        salesToDate: 625.75,
        firstYearValue: 648.07,
        conversions: 3,
        clicks: 108,
        impressions: 3806,
        budget: 359,
      },
      reorder: {
        spend: 34.52,
        firstOrderSales: 602.5,
        salesToDate: 602.5,
        firstYearValue: 602.5,
        conversions: 2,
        clicks: 11,
        impressions: 638,
        budget: 41,
      },
    },
    {
      month: "2025-10",
      pooledBudget: 400,
      new: {
        spend: 182.65,
        firstOrderSales: 607.5,
        salesToDate: 1962.25,
        firstYearValue: 2036.82,
        conversions: 3,
        clicks: 118,
        impressions: 3582,
        budget: 337,
      },
      reorder: {
        spend: 34.08,
        firstOrderSales: 228.5,
        salesToDate: 228.5,
        firstYearValue: 228.5,
        conversions: 1,
        clicks: 15,
        impressions: 874,
        budget: 63,
      },
    },
    {
      month: "2025-11",
      pooledBudget: 400,
      new: {
        spend: 149.41,
        firstOrderSales: 299,
        salesToDate: 299,
        firstYearValue: 385.86,
        conversions: 2,
        clicks: 90,
        impressions: 3526,
        budget: 313,
      },
      reorder: {
        spend: 41.46,
        firstOrderSales: 208,
        salesToDate: 208,
        firstYearValue: 208,
        conversions: 1,
        clicks: 12,
        impressions: 672,
        budget: 87,
      },
    },
    {
      month: "2025-12",
      pooledBudget: 400,
      new: {
        spend: 125.33,
        firstOrderSales: 1223.5,
        salesToDate: 2257.48,
        firstYearValue: 2392.3,
        conversions: 3,
        clicks: 42,
        impressions: 1866,
        budget: 280,
      },
      reorder: {
        spend: 53.82,
        firstOrderSales: 452.5,
        salesToDate: 452.5,
        firstYearValue: 452.5,
        conversions: 2,
        clicks: 19,
        impressions: 824,
        budget: 120,
      },
    },
    {
      month: "2026-01",
      pooledBudget: 400,
      new: {
        spend: 168.36,
        firstOrderSales: 337.5,
        salesToDate: 465.5,
        firstYearValue: 585.34,
        conversions: 2,
        clicks: 76,
        impressions: 4059,
        budget: 295,
      },
      reorder: {
        spend: 60.05,
        firstOrderSales: 634.6,
        salesToDate: 634.6,
        firstYearValue: 634.6,
        conversions: 2,
        clicks: 10,
        impressions: 1199,
        budget: 105,
      },
    },
    {
      month: "2026-02",
      pooledBudget: 400,
      new: {
        spend: 156.12,
        firstOrderSales: 313,
        salesToDate: 540.5,
        firstYearValue: 603.06,
        conversions: 1,
        clicks: 50,
        impressions: 2161,
        budget: 348,
      },
      reorder: {
        spend: 23.2,
        firstOrderSales: 186.5,
        salesToDate: 186.5,
        firstYearValue: 186.5,
        conversions: 1,
        clicks: 9,
        impressions: 671,
        budget: 52,
      },
    },
    {
      month: "2026-03",
      pooledBudget: 400,
      new: {
        spend: 120.34,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 56,
        impressions: 2715,
        budget: 286,
      },
      reorder: {
        spend: 47.68,
        firstOrderSales: 389.5,
        salesToDate: 389.5,
        firstYearValue: 389.5,
        conversions: 2,
        clicks: 14,
        impressions: 892,
        budget: 114,
      },
    },
    {
      month: "2026-04",
      pooledBudget: 400,
      new: {
        spend: 170.15,
        firstOrderSales: 559.5,
        salesToDate: 559.5,
        firstYearValue: 726.06,
        conversions: 2,
        clicks: 66,
        impressions: 2804,
        budget: 329,
      },
      reorder: {
        spend: 36.51,
        firstOrderSales: 389.5,
        salesToDate: 389.5,
        firstYearValue: 389.5,
        conversions: 2,
        clicks: 9,
        impressions: 597,
        budget: 71,
      },
    },
    {
      month: "2026-05",
      pooledBudget: 400,
      new: {
        spend: 147.2,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 75,
        impressions: 2678,
        budget: 301,
      },
      reorder: {
        spend: 48.69,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 12,
        impressions: 537,
        budget: 99,
      },
    },
    {
      month: "2026-06",
      pooledBudget: 400,
      new: {
        spend: 142.63,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 72,
        impressions: 2063,
        budget: 308,
      },
      reorder: {
        spend: 42.86,
        firstOrderSales: 536.5,
        salesToDate: 536.5,
        firstYearValue: 536.5,
        conversions: 2,
        clicks: 5,
        impressions: 407,
        budget: 92,
      },
    },
    {
      month: "2026-07",
      pooledBudget: 400,
      new: {
        spend: 200.17,
        firstOrderSales: 160,
        salesToDate: 310,
        firstYearValue: 385.56,
        conversions: 1,
        clicks: 59,
        impressions: 2399,
        budget: 234,
      },
      reorder: {
        spend: 142.02,
        firstOrderSales: 1381.48,
        salesToDate: 1381.48,
        firstYearValue: 1381.48,
        conversions: 3,
        clicks: 15,
        impressions: 552,
        budget: 166,
      },
    },
    {
      month: "2026-08",
      pooledBudget: 400,
      new: {
        spend: 155.81,
        firstOrderSales: 458.5,
        salesToDate: 458.5,
        firstYearValue: 719.53,
        conversions: 3,
        clicks: 45,
        impressions: 2568,
        budget: 293,
      },
      reorder: {
        spend: 57.2,
        firstOrderSales: 296,
        salesToDate: 296,
        firstYearValue: 296,
        conversions: 2,
        clicks: 7,
        impressions: 644,
        budget: 107,
      },
    },
  ],
  current: {
    month: "2026-09",
    pooledBudget: 400,
    projectedUtilization: 0.86,
    new: {
      spend: 147.06,
      firstOrderSales: 542.62,
      salesToDate: 542.62,
      firstYearValue: 661.69,
      conversions: 2,
      clicks: 34,
      impressions: 1880,
      budget: 302,
    },
    reorder: {
      spend: 47.57,
      firstOrderSales: 140,
      salesToDate: 140,
      firstYearValue: 140,
      conversions: 1,
      clicks: 7,
      impressions: 327,
      budget: 98,
    },
  },
};

/**
 * Kaari & Co. (b_7xqoxri3cq, advertiser 1756243082779176848).
 * Real numbers (complete months Aug 2025 – Jul 2026, of a 31-month tenure that
 * started Feb 2024). NEW_AND_RETURNING, both campaigns RUNNING. Scaling-up
 * archetype: new-customer spend quadrupled across the last quarter ($102 in Apr
 * to $584 in Jul, with Aug already at $448 and 11 conversions) while the reorder
 * campaign stays nearly dormant at 5 lifetime conversions. The platform is the
 * only one of these three eligible for a budget increase, recommending $920.
 *
 * Sales are 100% real per the platform, from
 * MANAGER_METRICS_CAMPAIGN_PERIODS:
 *   - firstOrderSales = TOTAL_SALES_LOCAL_CENTS (immediate first-order sales)
 *   - salesToDate     = TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS
 *   - firstYearValue  = BEST_ESTIMATE_1YR_TOTAL_SALES..._FROM_ADS_ACQUIRED_CUSTOMERS
 * The reorder campaign reports 0 in the two acquired-customer columns, so its
 * reorder ad sales come from TOTAL_SALES and first-order = sales-to-date =
 * first-year.
 *
 * `budget` is the standing budget recommendation ($920 new + $100 reorder); no
 * reported budget exists for any month shown (see ELANA_GABRIELLE above). That
 * makes this the under-utilized-looking one of the three even while it scales.
 */
const KAARI_CO: RealBrandConfig = {
  profileToken: "adv_kaari_co",
  tokenPrefix: "kc",
  aov: 236,
  anchorLabel: "Aug 18, 2026",
  overview: {
    numCustomersAcquired: 60,
    numReorderConversions: 5,
    numTotalConversions: 65,
    totalAdsSalesDollars: 23334,
    newCustomerAdsSalesDollars: 21105,
    reorderAdsSalesDollars: 2229,
    totalAdsSpendDollars: 5366,
    newCustomerAdsSpendDollars: 5088,
    reorderAdsSpendDollars: 278,
    newCustomerBestEstimateFirstYearRoas: 3.75,
    reorderRoas: 8.03,
    combinedFirstYearRoas: 3.97,
  },
  months: [
    {
      month: "2025-08",
      new: {
        spend: 74.36,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 32,
        impressions: 1639,
        budget: 920,
      },
      reorder: {
        spend: 6.13,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 2,
        impressions: 116,
        budget: 100,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 126.35,
        firstOrderSales: 204,
        salesToDate: 312,
        firstYearValue: 320.08,
        conversions: 1,
        clicks: 41,
        impressions: 2249,
        budget: 920,
      },
      reorder: {
        spend: 7.21,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 2,
        impressions: 286,
        budget: 100,
      },
    },
    {
      month: "2025-10",
      new: {
        spend: 180.55,
        firstOrderSales: 350,
        salesToDate: 742,
        firstYearValue: 772.57,
        conversions: 2,
        clicks: 39,
        impressions: 2234,
        budget: 920,
      },
      reorder: {
        spend: 9.38,
        firstOrderSales: 183,
        salesToDate: 183,
        firstYearValue: 183,
        conversions: 1,
        clicks: 2,
        impressions: 143,
        budget: 100,
      },
    },
    {
      month: "2025-11",
      new: {
        spend: 170.54,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 32,
        impressions: 1798,
        budget: 920,
      },
      reorder: {
        spend: 32.13,
        firstOrderSales: 360,
        salesToDate: 360,
        firstYearValue: 360,
        conversions: 1,
        clicks: 6,
        impressions: 149,
        budget: 100,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 123.74,
        firstOrderSales: 212,
        salesToDate: 212,
        firstYearValue: 228.39,
        conversions: 1,
        clicks: 31,
        impressions: 1304,
        budget: 920,
      },
      reorder: {
        spend: 4.58,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 1,
        impressions: 116,
        budget: 100,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 166.82,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 41,
        impressions: 2759,
        budget: 920,
      },
      reorder: {
        spend: 23.52,
        firstOrderSales: 675,
        salesToDate: 675,
        firstYearValue: 675,
        conversions: 1,
        clicks: 6,
        impressions: 386,
        budget: 100,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 54.29,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 17,
        impressions: 1162,
        budget: 920,
      },
      reorder: {
        spend: 11.89,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 173,
        budget: 100,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 113.07,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 27,
        impressions: 1399,
        budget: 920,
      },
      reorder: {
        spend: 6.13,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 250,
        budget: 100,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 101.94,
        firstOrderSales: 230,
        salesToDate: 230,
        firstYearValue: 259.12,
        conversions: 1,
        clicks: 19,
        impressions: 1238,
        budget: 920,
      },
      reorder: {
        spend: 1.54,
        firstOrderSales: 228,
        salesToDate: 228,
        firstYearValue: 228,
        conversions: 1,
        clicks: 1,
        impressions: 174,
        budget: 100,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 247.31,
        firstOrderSales: 360,
        salesToDate: 576,
        firstYearValue: 681.24,
        conversions: 2,
        clicks: 34,
        impressions: 1114,
        budget: 920,
      },
      reorder: {
        spend: 6.41,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 2,
        impressions: 208,
        budget: 100,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 335.49,
        firstOrderSales: 210,
        salesToDate: 550,
        firstYearValue: 600.46,
        conversions: 1,
        clicks: 48,
        impressions: 1892,
        budget: 920,
      },
      reorder: {
        spend: 15.65,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 2,
        impressions: 222,
        budget: 100,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 583.85,
        firstOrderSales: 1140,
        salesToDate: 1140,
        firstYearValue: 1411.86,
        conversions: 5,
        clicks: 74,
        impressions: 2447,
        budget: 920,
      },
      reorder: {
        spend: 47.76,
        firstOrderSales: 783,
        salesToDate: 783,
        firstYearValue: 783,
        conversions: 1,
        clicks: 5,
        impressions: 290,
        budget: 100,
      },
    },
  ],
  current: {
    month: "2026-08",
    projectedUtilization: 0.77,
    new: {
      spend: 448.05,
      firstOrderSales: 2465.1,
      salesToDate: 2465.1,
      firstYearValue: 2722.41,
      conversions: 11,
      clicks: 70,
      impressions: 2647,
      budget: 920,
    },
    reorder: {
      spend: 8.14,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 5,
      impressions: 378,
      budget: 100,
    },
  },
};

/**
 * The Woodsy Craft Co. (b_atnn44mpxf, advertiser -7828252857858171118).
 * Real numbers (complete months Aug 2025 – Jul 2026, of a 23-month tenure that
 * started Oct 2024). NEW_AND_RETURNING, both campaigns RUNNING. Smallest and
 * lumpiest of the three: 17 lifetime acquisitions across 19 active months, five
 * zero-conversion months in the year shown, but the highest first-year ROAS at
 * 6.1x — one Oct 2025 customer alone is worth $2,613 to date. A DIY craft-kit
 * brand, so the mix is gift stores and bookstores almost exclusively.
 *
 * Sales are 100% real per the platform, from
 * MANAGER_METRICS_CAMPAIGN_PERIODS:
 *   - firstOrderSales = TOTAL_SALES_LOCAL_CENTS (immediate first-order sales)
 *   - salesToDate     = TOTAL_SALES_LOCAL_CENTS_FROM_ADS_ACQUIRED_CUSTOMERS
 *   - firstYearValue  = BEST_ESTIMATE_1YR_TOTAL_SALES..._FROM_ADS_ACQUIRED_CUSTOMERS
 * The reorder campaign reports 0 in the two acquired-customer columns, so its
 * reorder ad sales come from TOTAL_SALES and first-order = sales-to-date =
 * first-year.
 *
 * `budget` is the standing budget recommendation ($300 new + $100 reorder).
 * Reported budgets do exist for this advertiser through Apr 2026 but stop after
 * that, so a flat recommendation keeps the whole year on one basis rather than
 * switching mid-chart.
 */
const WOODSY_CRAFT: RealBrandConfig = {
  profileToken: "adv_woodsy_craft",
  tokenPrefix: "wc",
  aov: 312,
  anchorLabel: "Aug 18, 2026",
  overview: {
    numCustomersAcquired: 17,
    numReorderConversions: 14,
    numTotalConversions: 31,
    totalAdsSalesDollars: 12010,
    newCustomerAdsSalesDollars: 8858,
    reorderAdsSalesDollars: 3152,
    totalAdsSpendDollars: 2216,
    newCustomerAdsSpendDollars: 1874,
    reorderAdsSpendDollars: 343,
    newCustomerBestEstimateFirstYearRoas: 6.08,
    reorderRoas: 9.2,
    combinedFirstYearRoas: 6.56,
  },
  months: [
    {
      month: "2025-08",
      new: {
        spend: 100.28,
        firstOrderSales: 195,
        salesToDate: 195,
        firstYearValue: 202.54,
        conversions: 1,
        clicks: 50,
        impressions: 2592,
        budget: 300,
      },
      reorder: {
        spend: 16.09,
        firstOrderSales: 420,
        salesToDate: 420,
        firstYearValue: 420,
        conversions: 2,
        clicks: 5,
        impressions: 142,
        budget: 100,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 69.55,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 37,
        impressions: 2216,
        budget: 300,
      },
      reorder: {
        spend: 17.34,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 168,
        budget: 100,
      },
    },
    {
      month: "2025-10",
      new: {
        spend: 85.38,
        firstOrderSales: 1542.5,
        salesToDate: 2612.5,
        firstYearValue: 2868.07,
        conversions: 2,
        clicks: 29,
        impressions: 1791,
        budget: 300,
      },
      reorder: {
        spend: 34.4,
        firstOrderSales: 342.5,
        salesToDate: 342.5,
        firstYearValue: 342.5,
        conversions: 1,
        clicks: 7,
        impressions: 228,
        budget: 100,
      },
    },
    {
      month: "2025-11",
      new: {
        spend: 127.65,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 41,
        impressions: 2396,
        budget: 300,
      },
      reorder: {
        spend: 8.95,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 203,
        budget: 100,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 43.99,
        firstOrderSales: 327.5,
        salesToDate: 327.5,
        firstYearValue: 470.97,
        conversions: 1,
        clicks: 21,
        impressions: 1160,
        budget: 300,
      },
      reorder: {
        spend: 8.18,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 223,
        budget: 100,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 88.86,
        firstOrderSales: 243.2,
        salesToDate: 811,
        firstYearValue: 984.51,
        conversions: 1,
        clicks: 21,
        impressions: 1383,
        budget: 300,
      },
      reorder: {
        spend: 36.22,
        firstOrderSales: 455.5,
        salesToDate: 455.5,
        firstYearValue: 455.5,
        conversions: 2,
        clicks: 5,
        impressions: 336,
        budget: 100,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 21.81,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 9,
        impressions: 745,
        budget: 300,
      },
      reorder: {
        spend: 14.98,
        firstOrderSales: 384,
        salesToDate: 384,
        firstYearValue: 384,
        conversions: 2,
        clicks: 4,
        impressions: 163,
        budget: 100,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 50.37,
        firstOrderSales: 255,
        salesToDate: 255,
        firstYearValue: 465.74,
        conversions: 1,
        clicks: 18,
        impressions: 1366,
        budget: 300,
      },
      reorder: {
        spend: 4.59,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 2,
        impressions: 311,
        budget: 100,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 68.34,
        firstOrderSales: 152,
        salesToDate: 272,
        firstYearValue: 483.8,
        conversions: 1,
        clicks: 16,
        impressions: 1681,
        budget: 300,
      },
      reorder: {
        spend: 16.19,
        firstOrderSales: 184,
        salesToDate: 184,
        firstYearValue: 184,
        conversions: 1,
        clicks: 4,
        impressions: 263,
        budget: 100,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 233.09,
        firstOrderSales: 472,
        salesToDate: 472,
        firstYearValue: 942.37,
        conversions: 2,
        clicks: 29,
        impressions: 2140,
        budget: 300,
      },
      reorder: {
        spend: 16.79,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 164,
        budget: 100,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 217.16,
        firstOrderSales: 676,
        salesToDate: 964,
        firstYearValue: 1492.56,
        conversions: 2,
        clicks: 20,
        impressions: 2063,
        budget: 300,
      },
      reorder: {
        spend: 3.57,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 1,
        impressions: 178,
        budget: 100,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 349.1,
        firstOrderSales: 568.8,
        salesToDate: 568.8,
        firstYearValue: 1107.77,
        conversions: 2,
        clicks: 38,
        impressions: 3109,
        budget: 300,
      },
      reorder: {
        spend: 50.89,
        firstOrderSales: 493.6,
        salesToDate: 493.6,
        firstYearValue: 493.6,
        conversions: 2,
        clicks: 5,
        impressions: 275,
        budget: 100,
      },
    },
  ],
  current: {
    month: "2026-08",
    projectedUtilization: 0.68,
    new: {
      spend: 109.56,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 27,
      impressions: 1908,
      budget: 300,
    },
    reorder: {
      spend: 49.06,
      firstOrderSales: 384,
      salesToDate: 384,
      firstYearValue: 384,
      conversions: 1,
      clicks: 5,
      impressions: 195,
      budget: 100,
    },
  },
};

/**
 * Zotter Chocolates (b_d7zqrbdq, advertiser -2425147271178345382).
 *
 * The only variant carrying its whole tenure — 29 complete months from Mar 2024,
 * not the trailing twelve the others hold. The reorder campaign is why: it ran
 * Feb–Jun 2025 and stopped, which is outside a twelve-month window, so a trailing
 * cut would report 13 reorder conversions in the headline metrics that no month
 * row could account for. The charts still plot the trailing thirteen months
 * (LIFETIME_CHART_MONTHS) and page back through the rest.
 *
 * Both segments are 100% real, including reorder sales — nothing here is modeled
 * as `reorderConversions * aov`, so `aov` only backs the customer-row generator.
 */
const ZOTTER_CHOCOLATES: RealBrandConfig = {
  profileToken: "adv_zotter_chocolates",
  tokenPrefix: "zc",
  aov: 368,
  // Derived from the data, not the calendar: Aug 2026 prorated budget of $723 is
  // exactly 28/31 of the $800 monthly budget.
  anchorLabel: "Aug 28, 2026",
  overview: {
    numCustomersAcquired: 69,
    numReorderConversions: 13,
    numTotalConversions: 82,
    totalAdsSalesDollars: 63054,
    newCustomerAdsSalesDollars: 59559,
    reorderAdsSalesDollars: 3495,
    totalAdsSpendDollars: 7361,
    newCustomerAdsSpendDollars: 7014,
    reorderAdsSpendDollars: 347,
    newCustomerBestEstimateFirstYearRoas: 7.74,
    reorderRoas: 10.07,
    combinedFirstYearRoas: 7.85,
  },
  months: [
    {
      month: "2024-03",
      new: {
        spend: 366,
        firstOrderSales: 1087,
        salesToDate: 6043,
        firstYearValue: 4307,
        conversions: 6,
        clicks: 136,
        impressions: 7253,
        budget: 619,
      },
    },
    {
      month: "2024-04",
      new: {
        spend: 455,
        firstOrderSales: 2778,
        salesToDate: 13145,
        firstYearValue: 8364,
        conversions: 7,
        clicks: 161,
        impressions: 10118,
        budget: 800,
      },
    },
    {
      month: "2024-05",
      new: {
        spend: 288,
        firstOrderSales: 1616,
        salesToDate: 6643,
        firstYearValue: 4777,
        conversions: 7,
        clicks: 114,
        impressions: 6063,
        budget: 800,
      },
    },
    {
      month: "2024-06",
      new: {
        spend: 174,
        firstOrderSales: 247,
        salesToDate: 563,
        firstYearValue: 563,
        conversions: 2,
        clicks: 66,
        impressions: 3317,
        budget: 800,
      },
    },
    {
      month: "2024-07",
      new: {
        spend: 74,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 124,
        impressions: 8541,
        budget: 800,
      },
    },
    {
      month: "2024-08",
      new: {
        spend: 273,
        firstOrderSales: 2272,
        salesToDate: 2272,
        firstYearValue: 2272,
        conversions: 2,
        clicks: 96,
        impressions: 4574,
        budget: 800,
      },
    },
    {
      month: "2024-09",
      new: {
        spend: 99,
        firstOrderSales: 255,
        salesToDate: 255,
        firstYearValue: 255,
        conversions: 1,
        clicks: 66,
        impressions: 3549,
        budget: 800,
      },
    },
    {
      month: "2024-10",
      new: {
        spend: 103,
        firstOrderSales: 784,
        salesToDate: 2838,
        firstYearValue: 2838,
        conversions: 1,
        clicks: 98,
        impressions: 6117,
        budget: 800,
      },
    },
    {
      month: "2024-11",
      new: {
        spend: 220,
        firstOrderSales: 2245,
        salesToDate: 3003,
        firstYearValue: 2471,
        conversions: 4,
        clicks: 78,
        impressions: 3371,
        budget: 800,
      },
    },
    {
      month: "2024-12",
      new: {
        spend: 74,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 48,
        impressions: 1503,
        budget: 800,
      },
    },
    {
      month: "2025-01",
      new: {
        spend: 216,
        firstOrderSales: 1405,
        salesToDate: 2581,
        firstYearValue: 2581,
        conversions: 5,
        clicks: 74,
        impressions: 2836,
        budget: 800,
      },
    },
    {
      // Reorder ads start here. Their first month bought clicks and no
      // conversions, which is why the segment opens on a zero.
      month: "2025-02",
      new: {
        spend: 96,
        firstOrderSales: 1054,
        salesToDate: 3684,
        firstYearValue: 2644,
        conversions: 2,
        clicks: 33,
        impressions: 1154,
        budget: 617,
      },
      reorder: {
        spend: 19,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 212,
        budget: 183,
      },
    },
    {
      month: "2025-03",
      new: {
        spend: 179,
        firstOrderSales: 1458,
        salesToDate: 3710,
        firstYearValue: 3710,
        conversions: 5,
        clicks: 62,
        impressions: 3238,
        budget: 288,
      },
      reorder: {
        spend: 139,
        firstOrderSales: 900,
        salesToDate: 900,
        firstYearValue: 900,
        conversions: 4,
        clicks: 24,
        impressions: 1205,
        budget: 512,
      },
    },
    {
      month: "2025-04",
      new: {
        spend: 129,
        firstOrderSales: 906,
        salesToDate: 2126,
        firstYearValue: 2126,
        conversions: 3,
        clicks: 46,
        impressions: 1673,
        budget: 315,
      },
      reorder: {
        spend: 37,
        firstOrderSales: 112,
        salesToDate: 112,
        firstYearValue: 112,
        conversions: 1,
        clicks: 14,
        impressions: 811,
        budget: 485,
      },
    },
    {
      month: "2025-05",
      new: {
        spend: 75,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 34,
        impressions: 1758,
        budget: 493,
      },
      reorder: {
        spend: 121,
        firstOrderSales: 2102,
        salesToDate: 2102,
        firstYearValue: 2102,
        conversions: 6,
        clicks: 26,
        impressions: 997,
        budget: 307,
      },
    },
    {
      // Last month of reorder ads; the campaign stopped Jun 25, 2025.
      month: "2025-06",
      new: {
        spend: 71,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 29,
        impressions: 1457,
        budget: 550,
      },
      reorder: {
        spend: 31,
        firstOrderSales: 381,
        salesToDate: 381,
        firstYearValue: 381,
        conversions: 2,
        clicks: 6,
        impressions: 441,
        budget: 250,
      },
    },
    {
      month: "2025-07",
      new: {
        spend: 74,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 62,
        impressions: 2425,
        budget: 800,
      },
    },
    {
      month: "2025-08",
      new: {
        spend: 130,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 127,
        impressions: 4919,
        budget: 800,
      },
    },
    {
      month: "2025-09",
      new: {
        spend: 734,
        firstOrderSales: 3452,
        salesToDate: 4665,
        firstYearValue: 5068,
        conversions: 6,
        clicks: 179,
        impressions: 6809,
        budget: 800,
      },
    },
    {
      month: "2025-10",
      new: {
        spend: 434,
        firstOrderSales: 1021,
        salesToDate: 1864,
        firstYearValue: 2091,
        conversions: 3,
        clicks: 187,
        impressions: 7978,
        budget: 800,
      },
    },
    {
      month: "2025-11",
      new: {
        spend: 196,
        firstOrderSales: 393,
        salesToDate: 393,
        firstYearValue: 562,
        conversions: 2,
        clicks: 166,
        impressions: 6186,
        budget: 800,
      },
    },
    {
      month: "2025-12",
      new: {
        spend: 176,
        firstOrderSales: 130,
        salesToDate: 259,
        firstYearValue: 373,
        conversions: 1,
        clicks: 115,
        impressions: 4124,
        budget: 800,
      },
    },
    {
      month: "2026-01",
      new: {
        spend: 495,
        firstOrderSales: 1246,
        salesToDate: 1635,
        firstYearValue: 2517,
        conversions: 5,
        clicks: 106,
        impressions: 4599,
        budget: 800,
      },
    },
    {
      month: "2026-02",
      new: {
        spend: 146,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 31,
        impressions: 2111,
        budget: 800,
      },
    },
    {
      month: "2026-03",
      new: {
        spend: 176,
        firstOrderSales: 261,
        salesToDate: 261,
        firstYearValue: 502,
        conversions: 1,
        clicks: 33,
        impressions: 2007,
        budget: 800,
      },
    },
    {
      month: "2026-04",
      new: {
        spend: 200,
        firstOrderSales: 2123,
        salesToDate: 2642,
        firstYearValue: 4052,
        conversions: 3,
        clicks: 44,
        impressions: 2193,
        budget: 800,
      },
    },
    {
      month: "2026-05",
      new: {
        spend: 267,
        firstOrderSales: 198,
        salesToDate: 328,
        firstYearValue: 599,
        conversions: 1,
        clicks: 45,
        impressions: 2174,
        budget: 800,
      },
    },
    {
      month: "2026-06",
      new: {
        spend: 394,
        firstOrderSales: 194,
        salesToDate: 194,
        firstYearValue: 508,
        conversions: 1,
        clicks: 41,
        impressions: 2812,
        budget: 800,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 701,
        firstOrderSales: 454,
        salesToDate: 454,
        firstYearValue: 1083,
        conversions: 1,
        clicks: 60,
        impressions: 3567,
        budget: 800,
      },
    },
  ],
  current: {
    month: "2026-08",
    // $612 across 28 days annualizes to ~$678 of the $800 budget.
    projectedUtilization: 0.85,
    new: {
      spend: 612,
      firstOrderSales: 259,
      salesToDate: 259,
      firstYearValue: 748,
      conversions: 1,
      clicks: 52,
      impressions: 2572,
      budget: 723,
    },
  },
};

/**
 * Dairy Brand (b_2dvofcgxz7, advertiser 8749774711315878893).
 * Real numbers, complete months Feb 2024 – Aug 2026 plus a partial Sep 2026.
 *
 * The largest program in the set by an order of magnitude: $65k of spend
 * returning $454k across 1,075 conversions, where the next biggest variant is
 * Zotter at $7k and 82. Both segments are 100% real, including reorder sales —
 * nothing here is modeled as `reorderConversions * aov`, so `aov` only backs the
 * customer-row generator.
 *
 * Alone among the variants, the new segment's `salesToDate` is COHORT-EXACT: it
 * is that acquisition month's first orders plus the reorders those same retailers
 * placed over the following twelve months, read straight off
 * CONTINUED_SALES_BY_COHORT. So reorder sales in the month table equals the
 * cohort chart to the dollar ($280,149 over the 31 complete months). Every other
 * variant sources `salesToDate` from CAMPAIGN_PERIODS, which buckets continued
 * sales by the month the reorder landed rather than by acquisition month.
 *
 * Because the cohort basis stops at twelve months, the overview totals are the
 * sum of these months rather than the platform's uncapped lifetime rollup — that
 * is $378,633 of new-customer sales here against $455,724 lifetime. ROAS is
 * unaffected; it runs off `firstYearValue`, which was already cohort-scoped.
 *
 * Carries its whole tenure — 31 complete months — rather than the trailing twelve
 * most variants hold. The reorder campaign started a year into the program
 * (Feb 2025), so a trailing cut would show both segments running in every row and
 * hide the switch-on entirely, which is the one thing this brand's history shows
 * that no other variant's does. The charts still plot the trailing thirteen
 * months (LIFETIME_CHART_MONTHS) and page back through the rest.
 *
 * The budget is POOLED: one $6,000 pool serves both campaigns, so `pooledBudget`
 * is set on every month and the per-segment `budget` values are that month's
 * prorated split. CAMPAIGN_PERIODS stops reporting `prorated_budget_local` after
 * Jun 2025, so from Jul 2025 the pool is the advertiser's standing $6,000 and the
 * split is apportioned by that month's spend. Only `pooledBudget` is displayed —
 * the segment figures are a fallback nothing currently reads.
 */
const DAIRY_BRAND: RealBrandConfig = {
  profileToken: "adv_dairy_brand",
  tokenPrefix: "db",
  // $457,399 of lifetime new-customer sales over 2,658 ad-attributed orders. That
  // lifetime figure is uncapped, so it backs `aov` but not the totals below.
  aov: 172,
  // Snapshot ds 2026-09-17, which is also the last attributed order date.
  anchorLabel: "Sep 17, 2026",
  overview: {
    numCustomersAcquired: 663,
    numReorderConversions: 412,
    numTotalConversions: 1075,
    totalAdsSalesDollars: 454071,
    newCustomerAdsSalesDollars: 378633,
    reorderAdsSalesDollars: 75439,
    totalAdsSpendDollars: 64938,
    newCustomerAdsSpendDollars: 54215,
    reorderAdsSpendDollars: 10723,
    newCustomerBestEstimateFirstYearRoas: 7.36,
    reorderRoas: 7.03,
    combinedFirstYearRoas: 7.3,
  },
  months: [
    {
      month: "2024-02",
      pooledBudget: 4138,
      new: {
        spend: 748.98,
        firstOrderSales: 1263.36,
        salesToDate: 4158.36,
        firstYearValue: 5205.67,
        conversions: 11,
        clicks: 186,
        impressions: 6725,
        budget: 4138,
      },
    },
    {
      month: "2024-03",
      pooledBudget: 2258,
      new: {
        spend: 1004.8,
        firstOrderSales: 2163.08,
        salesToDate: 14829.08,
        firstYearValue: 13846.98,
        conversions: 11,
        clicks: 341,
        impressions: 12720,
        budget: 2258,
      },
    },
    {
      month: "2024-04",
      pooledBudget: 2500,
      new: {
        spend: 1419.78,
        firstOrderSales: 3378.94,
        salesToDate: 14505.94,
        firstYearValue: 14821.95,
        conversions: 23,
        clicks: 406,
        impressions: 17789,
        budget: 2500,
      },
    },
    {
      month: "2024-05",
      pooledBudget: 5903,
      new: {
        spend: 920.1,
        firstOrderSales: 3064,
        salesToDate: 18802,
        firstYearValue: 18927.4,
        conversions: 23,
        clicks: 324,
        impressions: 12560,
        budget: 5903,
      },
    },
    {
      month: "2024-06",
      pooledBudget: 6000,
      new: {
        spend: 998.43,
        firstOrderSales: 4072.14,
        salesToDate: 19619.14,
        firstYearValue: 19949.96,
        conversions: 27,
        clicks: 339,
        impressions: 16593,
        budget: 6000,
      },
    },
    {
      month: "2024-07",
      pooledBudget: 6000,
      new: {
        spend: 2092.46,
        firstOrderSales: 7305.97,
        salesToDate: 27459.97,
        firstYearValue: 27944.81,
        conversions: 40,
        clicks: 639,
        impressions: 39294,
        budget: 6000,
      },
    },
    {
      month: "2024-08",
      pooledBudget: 6000,
      new: {
        spend: 1775.49,
        firstOrderSales: 3868.88,
        salesToDate: 13523.88,
        firstYearValue: 13837.44,
        conversions: 31,
        clicks: 548,
        impressions: 29994,
        budget: 6000,
      },
    },
    {
      month: "2024-09",
      pooledBudget: 6000,
      new: {
        spend: 1333.97,
        firstOrderSales: 1622.06,
        salesToDate: 10402.06,
        firstYearValue: 9937.11,
        conversions: 15,
        clicks: 417,
        impressions: 24425,
        budget: 6000,
      },
    },
    {
      month: "2024-10",
      pooledBudget: 6000,
      new: {
        spend: 1456.13,
        firstOrderSales: 4160.75,
        salesToDate: 8202.75,
        firstYearValue: 7151.17,
        conversions: 19,
        clicks: 452,
        impressions: 18466,
        budget: 6000,
      },
    },
    {
      month: "2024-11",
      pooledBudget: 6000,
      new: {
        spend: 1246.02,
        firstOrderSales: 3902.9,
        salesToDate: 10073.9,
        firstYearValue: 10183.25,
        conversions: 28,
        clicks: 386,
        impressions: 16358,
        budget: 6000,
      },
    },
    {
      month: "2024-12",
      pooledBudget: 6000,
      new: {
        spend: 870.44,
        firstOrderSales: 1399.89,
        salesToDate: 5239.89,
        firstYearValue: 4718.46,
        conversions: 13,
        clicks: 246,
        impressions: 9948,
        budget: 6000,
      },
    },
    {
      month: "2025-01",
      pooledBudget: 6000,
      new: {
        spend: 857.65,
        firstOrderSales: 3199.47,
        salesToDate: 11540.47,
        firstYearValue: 11059.86,
        conversions: 22,
        clicks: 257,
        impressions: 11718,
        budget: 6000,
      },
    },
    {
      month: "2025-02",
      pooledBudget: 6000,
      new: {
        spend: 564.41,
        firstOrderSales: 846.6,
        salesToDate: 5581.6,
        firstYearValue: 6375.79,
        conversions: 8,
        clicks: 170,
        impressions: 7882,
        budget: 4136,
      },
      reorder: {
        spend: 130.17,
        firstOrderSales: 1041.03,
        salesToDate: 1041.03,
        firstYearValue: 1041.03,
        conversions: 6,
        clicks: 20,
        impressions: 3431,
        budget: 1864,
      },
    },
    {
      month: "2025-03",
      pooledBudget: 6000,
      new: {
        spend: 639.58,
        firstOrderSales: 965.43,
        salesToDate: 7501.43,
        firstYearValue: 5272.17,
        conversions: 8,
        clicks: 192,
        impressions: 9792,
        budget: 2520,
      },
      reorder: {
        spend: 238.82,
        firstOrderSales: 2987.82,
        salesToDate: 2987.82,
        firstYearValue: 2987.82,
        conversions: 22,
        clicks: 38,
        impressions: 5518,
        budget: 3480,
      },
    },
    {
      month: "2025-04",
      pooledBudget: 6000,
      new: {
        spend: 690.29,
        firstOrderSales: 1392.08,
        salesToDate: 13380.08,
        firstYearValue: 13655.1,
        conversions: 14,
        clicks: 206,
        impressions: 8677,
        budget: 2574,
      },
      reorder: {
        spend: 235.59,
        firstOrderSales: 2577.75,
        salesToDate: 2577.75,
        firstYearValue: 2577.75,
        conversions: 17,
        clicks: 41,
        impressions: 3801,
        budget: 3426,
      },
    },
    {
      month: "2025-05",
      pooledBudget: 6000,
      new: {
        spend: 648.12,
        firstOrderSales: 2000.45,
        salesToDate: 12325.45,
        firstYearValue: 3819.33,
        conversions: 13,
        clicks: 183,
        impressions: 7616,
        budget: 2597,
      },
      reorder: {
        spend: 189.67,
        firstOrderSales: 2156.51,
        salesToDate: 2156.51,
        firstYearValue: 2156.51,
        conversions: 15,
        clicks: 32,
        impressions: 3530,
        budget: 3403,
      },
    },
    {
      month: "2025-06",
      pooledBudget: 2400,
      new: {
        spend: 763.99,
        firstOrderSales: 1437.04,
        salesToDate: 8688.04,
        firstYearValue: 8226.45,
        conversions: 11,
        clicks: 201,
        impressions: 8233,
        budget: 1006,
      },
      reorder: {
        spend: 173.33,
        firstOrderSales: 2258.09,
        salesToDate: 2258.09,
        firstYearValue: 2258.09,
        conversions: 16,
        clicks: 30,
        impressions: 3656,
        budget: 1394,
      },
    },
    {
      month: "2025-07",
      pooledBudget: 6000,
      new: {
        spend: 1280.45,
        firstOrderSales: 2432.45,
        salesToDate: 10528.45,
        firstYearValue: 9254.83,
        conversions: 20,
        clicks: 260,
        impressions: 14866,
        budget: 4563,
      },
      reorder: {
        spend: 403.27,
        firstOrderSales: 4446.44,
        salesToDate: 4446.44,
        firstYearValue: 4446.44,
        conversions: 22,
        clicks: 57,
        impressions: 5318,
        budget: 1437,
      },
    },
    {
      month: "2025-08",
      pooledBudget: 6000,
      new: {
        spend: 2566.45,
        firstOrderSales: 2758.34,
        salesToDate: 16766.34,
        firstYearValue: 13776.12,
        conversions: 20,
        clicks: 423,
        impressions: 21075,
        budget: 5403,
      },
      reorder: {
        spend: 283.83,
        firstOrderSales: 1982.87,
        salesToDate: 1982.87,
        firstYearValue: 1982.87,
        conversions: 15,
        clicks: 36,
        impressions: 3493,
        budget: 597,
      },
    },
    {
      month: "2025-09",
      pooledBudget: 6000,
      new: {
        spend: 2877.56,
        firstOrderSales: 3743.45,
        salesToDate: 26583.45,
        firstYearValue: 28911.88,
        conversions: 22,
        clicks: 401,
        impressions: 21787,
        budget: 5414,
      },
      reorder: {
        spend: 311.63,
        firstOrderSales: 1650.53,
        salesToDate: 1650.53,
        firstYearValue: 1650.53,
        conversions: 11,
        clicks: 37,
        impressions: 3633,
        budget: 586,
      },
    },
    {
      month: "2025-10",
      pooledBudget: 6000,
      new: {
        spend: 2092.88,
        firstOrderSales: 2993.01,
        salesToDate: 13131.01,
        firstYearValue: 17041.87,
        conversions: 18,
        clicks: 410,
        impressions: 19601,
        budget: 4568,
      },
      reorder: {
        spend: 656.37,
        firstOrderSales: 5729.97,
        salesToDate: 5729.97,
        firstYearValue: 5729.97,
        conversions: 24,
        clicks: 65,
        impressions: 4283,
        budget: 1432,
      },
    },
    {
      month: "2025-11",
      pooledBudget: 6000,
      new: {
        spend: 3204.36,
        firstOrderSales: 5565.72,
        salesToDate: 23164.72,
        firstYearValue: 18681.43,
        conversions: 35,
        clicks: 521,
        impressions: 29900,
        budget: 4731,
      },
      reorder: {
        spend: 859.35,
        firstOrderSales: 6197.51,
        salesToDate: 6197.51,
        firstYearValue: 6197.51,
        conversions: 39,
        clicks: 90,
        impressions: 5117,
        budget: 1269,
      },
    },
    {
      month: "2025-12",
      pooledBudget: 6000,
      new: {
        spend: 2534.73,
        firstOrderSales: 4515.27,
        salesToDate: 8918.27,
        firstYearValue: 10919.84,
        conversions: 27,
        clicks: 414,
        impressions: 26322,
        budget: 5094,
      },
      reorder: {
        spend: 451.02,
        firstOrderSales: 3946.49,
        salesToDate: 3946.49,
        firstYearValue: 3946.49,
        conversions: 19,
        clicks: 52,
        impressions: 4572,
        budget: 906,
      },
    },
    {
      month: "2026-01",
      pooledBudget: 6000,
      new: {
        spend: 2068.78,
        firstOrderSales: 3721.13,
        salesToDate: 13767.13,
        firstYearValue: 15507.3,
        conversions: 27,
        clicks: 540,
        impressions: 36527,
        budget: 4035,
      },
      reorder: {
        spend: 1007.22,
        firstOrderSales: 7055.14,
        salesToDate: 7055.14,
        firstYearValue: 7055.14,
        conversions: 33,
        clicks: 92,
        impressions: 6844,
        budget: 1965,
      },
    },
    {
      month: "2026-02",
      pooledBudget: 6000,
      new: {
        spend: 1603.15,
        firstOrderSales: 3516.94,
        salesToDate: 6796.94,
        firstYearValue: 10417.36,
        conversions: 28,
        clicks: 346,
        impressions: 23126,
        budget: 4827,
      },
      reorder: {
        spend: 389.51,
        firstOrderSales: 3494.06,
        salesToDate: 3494.06,
        firstYearValue: 3494.06,
        conversions: 19,
        clicks: 50,
        impressions: 5330,
        budget: 1173,
      },
    },
    {
      month: "2026-03",
      pooledBudget: 6000,
      new: {
        spend: 1766.19,
        firstOrderSales: 3199.97,
        salesToDate: 10843.97,
        firstYearValue: 11754.85,
        conversions: 25,
        clicks: 460,
        impressions: 28574,
        budget: 4880,
      },
      reorder: {
        spend: 405.53,
        firstOrderSales: 2586.96,
        salesToDate: 2586.96,
        firstYearValue: 2586.96,
        conversions: 17,
        clicks: 47,
        impressions: 5122,
        budget: 1120,
      },
    },
    {
      month: "2026-04",
      pooledBudget: 6000,
      new: {
        spend: 2878.44,
        firstOrderSales: 3048.54,
        salesToDate: 9122.54,
        firstYearValue: 12879.38,
        conversions: 23,
        clicks: 334,
        impressions: 20300,
        budget: 5066,
      },
      reorder: {
        spend: 530.91,
        firstOrderSales: 2751.14,
        salesToDate: 2751.14,
        firstYearValue: 2751.14,
        conversions: 18,
        clicks: 48,
        impressions: 4735,
        budget: 934,
      },
    },
    {
      month: "2026-05",
      pooledBudget: 6000,
      new: {
        spend: 4132.62,
        firstOrderSales: 4551.72,
        salesToDate: 10702.72,
        firstYearValue: 15696.21,
        conversions: 28,
        clicks: 394,
        impressions: 22653,
        budget: 4890,
      },
      reorder: {
        spend: 938.27,
        firstOrderSales: 4289.3,
        salesToDate: 4289.3,
        firstYearValue: 4289.3,
        conversions: 25,
        clicks: 70,
        impressions: 5322,
        budget: 1110,
      },
    },
    {
      month: "2026-06",
      pooledBudget: 6000,
      new: {
        spend: 3158.13,
        firstOrderSales: 4979.14,
        salesToDate: 10516.14,
        firstYearValue: 17502.27,
        conversions: 31,
        clicks: 314,
        impressions: 17886,
        budget: 4801,
      },
      reorder: {
        spend: 788.53,
        firstOrderSales: 3794.48,
        salesToDate: 3794.48,
        firstYearValue: 3794.48,
        conversions: 21,
        clicks: 58,
        impressions: 4878,
        budget: 1199,
      },
    },
    {
      month: "2026-07",
      pooledBudget: 6000,
      new: {
        spend: 2626.23,
        firstOrderSales: 5049.23,
        salesToDate: 8960.23,
        firstYearValue: 15403.83,
        conversions: 29,
        clicks: 271,
        impressions: 15599,
        budget: 3885,
      },
      reorder: {
        spend: 1430.22,
        firstOrderSales: 8105.35,
        salesToDate: 8105.35,
        firstYearValue: 8105.35,
        conversions: 40,
        clicks: 79,
        impressions: 5895,
        budget: 2115,
      },
    },
    {
      month: "2026-08",
      pooledBudget: 6000,
      new: {
        spend: 3394.45,
        firstOrderSales: 2365.91,
        salesToDate: 2996.91,
        firstYearValue: 6208.82,
        conversions: 13,
        clicks: 330,
        impressions: 19360,
        budget: 4338,
      },
      reorder: {
        spend: 1300.19,
        firstOrderSales: 8387.16,
        salesToDate: 8387.16,
        firstYearValue: 8387.16,
        conversions: 33,
        clicks: 89,
        impressions: 6499,
        budget: 1662,
      },
    },
  ],
  current: {
    month: "2026-09",
    pooledBudget: 6000,
    projectedUtilization: 0.75,
    new: {
      spend: 1696.3,
      firstOrderSales: 1675.84,
      salesToDate: 1675.84,
      firstYearValue: 3848.95,
      conversions: 8,
      clicks: 182,
      impressions: 9350,
      budget: 3974,
    },
    reorder: {
      spend: 864.86,
      firstOrderSales: 4702.14,
      salesToDate: 4702.14,
      firstYearValue: 4702.14,
      conversions: 21,
      clicks: 64,
      impressions: 4096,
      budget: 2026,
    },
  },
};

/**
 * A month the advertiser ran no ads at all, so CAMPAIGN_PERIODS has no row for it.
 *
 * These are the one kind of month in this file that is NOT transcribed from a
 * query result — there is nothing to transcribe. They exist because the chart
 * plots `months` positionally: leave a gap out and the months either side render
 * as neighbours, which would show Do Good Candle Studio's thirteen dormant months
 * as a single step rather than the shutdown and restart they actually were.
 */
const dormantMonth = (month: string): RealBrandMonth => ({
  month,
  new: {
    spend: 0,
    firstOrderSales: 0,
    salesToDate: 0,
    firstYearValue: 0,
    conversions: 0,
    clicks: 0,
    impressions: 0,
    budget: 0,
  },
});

/** Do Good Candle Studio ran nothing between Apr 2025 and Jun 2026. */
const DO_GOOD_CANDLE_DORMANT_MONTHS = [
  "2025-05",
  "2025-06",
  "2025-07",
  "2025-08",
  "2025-09",
  "2025-10",
  "2025-11",
  "2025-12",
  "2026-01",
  "2026-02",
  "2026-03",
  "2026-04",
  "2026-05",
];

/**
 * Do Good Candle Studio (b_3umm7l0w4u, advertiser 3706895363597287505).
 * Real numbers, Apr 2024 – Aug 2026 plus a partial Sep 2026.
 *
 * The floor-budget end of the set and by far the emptiest profile here: $320 of
 * lifetime spend, 2 conversions, 3 ad-attributed orders, against Dairy Brand's
 * $60k and 1,028. Almost every series on the page is a flat zero line under this
 * profile. That is the profile's purpose — it is what Ads Manager looks like to a
 * brand whose ads have never really worked — so resist filling the zeros in.
 *
 * Its entire result is one month. Both conversions and all $1,167 of sales landed
 * in Apr 2024; the 28 months since have produced nothing at all. Apr 2024 is also
 * the only month carrying a continued-sales gap: $699 of first orders against
 * $1,167 to date, the $468 being a Sep 2024 reorder from one of the two retailers
 * acquired that month. CAMPAIGN_PERIODS buckets that reorder back onto the
 * acquisition month for this advertiser, so it does not appear in Sep 2024's row.
 *
 * Carries its whole tenure rather than a trailing year, for the same reason Dairy
 * Brand and Zotter do: the thirteen dormant months (May 2025 – May 2026) and the
 * Jun 2026 restart are the most distinctive thing about this brand's history, and
 * a twelve-month window would show only the restart. See `dormantMonth` for why
 * those months are present as explicit zeros.
 *
 * NON_POOLED, so `budget` is the real per-segment prorated figure rather than a
 * split of one pool. The reorder segment exists from Jun 2026 and is all zeros
 * apart from a trickle of impressions — the campaign is switched on and has never
 * spent.
 */
const DO_GOOD_CANDLE: RealBrandConfig = {
  profileToken: "adv_do_good_candle",
  tokenPrefix: "dgc",
  // $1,167 of new-customer sales over 3 ad-attributed orders. Only backs the
  // customer-row generator; no reorder GMV is modeled here because both segments
  // carry real per-month sales.
  aov: 389,
  // Spend restates on the 4th/11th/18th/25th, so the edge is Sep 18 even though
  // the snapshot is ds 2026-09-20.
  anchorLabel: "Sep 18, 2026",
  overview: {
    numCustomersAcquired: 2,
    numReorderConversions: 0,
    numTotalConversions: 2,
    totalAdsSalesDollars: 1167,
    newCustomerAdsSalesDollars: 1167,
    reorderAdsSalesDollars: 0,
    totalAdsSpendDollars: 320,
    newCustomerAdsSpendDollars: 320,
    reorderAdsSpendDollars: 0,
    newCustomerBestEstimateFirstYearRoas: 3.64,
    reorderRoas: 0,
    combinedFirstYearRoas: 3.64,
  },
  months: [
    {
      month: "2024-04",
      new: {
        spend: 22.11,
        firstOrderSales: 699,
        salesToDate: 1167,
        firstYearValue: 1167,
        conversions: 2,
        clicks: 10,
        impressions: 661,
        budget: 180,
      },
    },
    {
      month: "2024-05",
      new: {
        spend: 12.19,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 5,
        impressions: 648,
        budget: 200,
      },
    },
    {
      month: "2024-06",
      new: {
        spend: 9.62,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 293,
        budget: 200,
      },
    },
    {
      month: "2024-07",
      new: {
        spend: 34.61,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 13,
        impressions: 1627,
        budget: 200,
      },
    },
    {
      month: "2024-08",
      new: {
        spend: 31.77,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 12,
        impressions: 803,
        budget: 200,
      },
    },
    {
      month: "2024-09",
      new: {
        spend: 10.17,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 4,
        impressions: 394,
        budget: 200,
      },
    },
    {
      month: "2024-10",
      new: {
        spend: 8.82,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 210,
        budget: 200,
      },
    },
    {
      month: "2024-11",
      new: {
        spend: 0,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 0,
        impressions: 199,
        budget: 200,
      },
    },
    {
      month: "2024-12",
      new: {
        spend: 7.69,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 3,
        impressions: 115,
        budget: 200,
      },
    },
    {
      month: "2025-01",
      new: {
        spend: 13.75,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 5,
        impressions: 339,
        budget: 200,
      },
    },
    {
      month: "2025-02",
      new: {
        spend: 14.42,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 5,
        impressions: 137,
        budget: 200,
      },
    },
    {
      month: "2025-03",
      new: {
        spend: 1.95,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 1,
        impressions: 94,
        budget: 200,
      },
    },
    {
      // Last month before the shutdown, and it only ran four days — hence a
      // prorated $27 against the standing $200.
      month: "2025-04",
      new: {
        spend: 2.68,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 1,
        impressions: 8,
        budget: 27,
      },
    },
    ...DO_GOOD_CANDLE_DORMANT_MONTHS.map(dormantMonth),
    {
      // Restart. Both campaigns switched on Jun 25, so six days prorated.
      month: "2026-06",
      new: {
        spend: 0,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 0,
        impressions: 45,
        budget: 30,
      },
      reorder: {
        spend: 0,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 0,
        impressions: 4,
        budget: 20,
      },
    },
    {
      month: "2026-07",
      new: {
        spend: 19.57,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 7,
        impressions: 741,
        budget: 150,
      },
      reorder: {
        spend: 0,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 0,
        impressions: 63,
        budget: 100,
      },
    },
    {
      // The heaviest month the brand has ever run, at $131 — and it converted
      // nobody.
      month: "2026-08",
      new: {
        spend: 130.85,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 29,
        impressions: 1027,
        budget: 150,
      },
      reorder: {
        spend: 0,
        firstOrderSales: 0,
        salesToDate: 0,
        firstYearValue: 0,
        conversions: 0,
        clicks: 0,
        impressions: 16,
        budget: 100,
      },
    },
  ],
  current: {
    month: "2026-09",
    // $33 spent 19 days in, against a combined $250 of standing budget.
    projectedUtilization: 0.21,
    new: {
      spend: 33.1,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 9,
      impressions: 686,
      budget: 95,
    },
    reorder: {
      spend: 0,
      firstOrderSales: 0,
      salesToDate: 0,
      firstYearValue: 0,
      conversions: 0,
      clicks: 0,
      impressions: 29,
      budget: 63,
    },
  },
};

/**
 * Real reorder timing per acquisition cohort: reorder sales dollars keyed by
 * months since acquisition (1 = the month after acquisition).
 *
 * Source: ANALYTICS.ADS_SNAPSHOTS.MANAGER_METRICS_BRAND_ORDERS, which is
 * order-level (one row per ad-attributed order, with a retailer token and an
 * order date). A cohort is the month of a retailer's first attributed order;
 * every later order is bucketed at its real offset. See `snowflakeQueries.ts`
 * COHORT_CONTINUATION for the refresh query.
 *
 * Kept as one block rather than inline on each month so a refresh is a single
 * paste. Cohorts absent here have no attributed reorder, and the chart falls
 * back to the modeled curve for them — that's most cohorts of the smaller
 * brands, which have only a handful of reorders each. Dairy Brand (1,567
 * reorders), Totally Bamboo (1,136), Penguin Random House (240), and Zotter
 * Chocolates (69) are dense enough to carry a real month-by-month shape.
 *
 * Pulled 2026-08-20; Zotter Chocolates added 2026-08-30. Dairy Brand and
 * Elana Gabrielle refreshed 2026-09-19 against snapshot ds 2026-09-17.
 */
const CONTINUED_SALES_BY_COHORT: Record<
  string,
  Record<string, Record<number, number>>
> = {
  adv_totally_bamboo: {
    "2025-07": {
      1: 666,
      2: 846,
      3: 1062,
      4: 96,
      5: 240,
      6: 2010,
      9: 2916,
      10: 153,
      11: 456,
      12: 854,
    },
    "2025-08": {
      1: 1125,
      2: 426,
      3: 477,
      4: 636,
      5: 1460,
      6: 120,
      7: 762,
      8: 360,
      9: 642,
      10: 921,
      11: 1710,
    },
    "2025-09": {
      1: 276,
      2: 1482,
      3: 1218,
      4: 2199,
      5: 840,
      6: 1704,
      7: 651,
      8: 1440,
      9: 540,
      10: 450,
    },
    "2025-10": {
      1: 2535,
      2: 3057,
      3: 3452,
      4: 1058,
      5: 480,
      6: 1434,
      7: 975,
      8: 570,
      9: 3517,
      10: 120,
    },
    "2025-11": {
      1: 1191,
      2: 1672,
      3: 702,
      4: 615,
      5: 120,
      6: 420,
      7: 180,
      8: 1279,
      9: 330,
    },
    "2025-12": { 1: 180, 2: 324, 3: 306, 4: 732, 5: 285, 7: 609, 8: 177 },
    "2026-01": { 1: 180, 2: 705, 3: 1249, 4: 231, 5: 260, 6: 3903, 7: 267 },
    "2026-02": { 1: 492, 2: 324, 3: 741, 4: 663, 5: 1835, 6: 966 },
    "2026-03": { 1: 664, 2: 255, 3: 697, 4: 951 },
    "2026-04": { 1: 1584, 2: 696, 3: 1786, 4: 1011 },
    "2026-05": { 1: 910, 2: 990, 3: 100 },
    "2026-06": { 1: 1263, 2: 594 },
    "2026-07": { 1: 1848 },
  },
  adv_penguin_rh: {
    "2024-10": { 1: 175, 3: 148, 4: 507, 9: 433, 11: 533, 12: 250 },
    "2024-11": {
      1: 354,
      2: 112,
      4: 287,
      6: 412,
      7: 762,
      8: 165,
      9: 385,
      10: 1000,
      11: 348,
      12: 350,
    },
    "2024-12": {
      1: 362,
      3: 344,
      4: 159,
      5: 647,
      7: 590,
      8: 251,
      9: 335,
      10: 1327,
      11: 321,
    },
    "2025-01": { 1: 243, 5: 116, 6: 229, 7: 198, 8: 320, 11: 150, 12: 102 },
    "2025-02": { 2: 120, 3: 125, 5: 114, 6: 128, 8: 526, 9: 213, 11: 410 },
    "2025-03": {
      1: 211,
      2: 226,
      3: 114,
      5: 462,
      7: 104,
      8: 132,
      9: 456,
      10: 103,
      11: 109,
    },
    "2025-04": {
      2: 713,
      3: 393,
      4: 888,
      5: 910,
      6: 1291,
      7: 1666,
      8: 1219,
      9: 1258,
      10: 690,
      12: 926,
    },
    "2025-05": { 2: 104, 3: 507, 4: 107, 6: 508, 7: 108, 8: 221 },
    "2025-06": {
      1: 166,
      2: 1461,
      3: 2306,
      4: 244,
      5: 634,
      7: 204,
      8: 727,
      9: 185,
      10: 303,
      11: 212,
      12: 135,
    },
    "2025-07": {
      2: 206,
      3: 357,
      4: 260,
      5: 790,
      6: 153,
      8: 295,
      9: 334,
      11: 279,
      12: 426,
    },
    "2025-08": { 3: 671, 4: 355 },
    "2025-09": {
      1: 449,
      2: 1217,
      3: 758,
      4: 387,
      6: 559,
      7: 837,
      8: 107,
      9: 1138,
      11: 608,
    },
  },
  adv_good_book_company: {
    "2026-01": { 3: 112 },
    "2026-03": { 3: 771, 5: 1028 },
  },
  adv_malwest: {
    "2026-05": { 2: 289 },
  },
  adv_king_floyds: {
    "2025-09": { 4: 272, 6: 173 },
    "2026-02": { 3: 266 },
    "2026-04": { 1: 525 },
    "2026-05": { 3: 349 },
  },
  adv_oggi: {
    "2025-12": { 6: 165, 7: 305 },
    "2026-01": { 2: 801, 5: 411, 6: 1221 },
    "2026-02": { 5: 330 },
    "2026-04": { 3: 154 },
    "2026-05": { 3: 528 },
    "2026-06": { 1: 281 },
  },
  // 41 reorders across 16 cohorts — the densest of the three new brands, and the
  // only one where reorders land as late as month 10.
  adv_elana_gabrielle: {
    "2024-04": { 5: 222, 10: 118 },
    "2024-05": { 1: 371, 2: 350, 3: 192, 4: 429, 8: 889 },
    "2024-07": { 3: 273, 6: 143, 7: 221 },
    "2025-01": { 4: 189, 5: 156 },
    "2025-02": { 1: 260 },
    "2025-04": { 7: 156 },
    "2025-05": { 3: 432, 10: 276 },
    "2025-07": { 1: 234, 2: 715, 10: 104 },
    "2025-08": { 1: 129, 3: 558 },
    "2025-09": { 2: 208, 7: 246, 10: 317, 12: 152 },
    "2025-10": { 2: 806, 3: 284, 6: 102 },
    "2025-11": { 1: 333, 6: 114 },
    "2025-12": { 1: 382, 6: 222, 7: 198, 9: 126 },
    "2026-01": { 6: 128 },
    "2026-02": { 5: 228 },
    "2026-07": { 1: 150 },
  },
  adv_kaari_co: {
    "2025-09": { 1: 108 },
    "2025-10": { 3: 251, 5: 141 },
    "2025-11": { 2: 675 },
    "2026-05": { 2: 216 },
    "2026-06": { 1: 225 },
  },
  adv_woodsy_craft: {
    "2025-10": { 5: 542, 9: 272 },
    "2025-11": { 3: 160, 8: 96 },
    "2026-01": { 6: 568 },
    "2026-04": { 2: 120 },
    "2026-06": { 1: 288 },
  },
  // 69 reorders across 16 cohorts — dense enough for a real shape at every
  // offset from 1 to 12, which none of the other small brands manage. The 2024
  // cohorts are the fullest, having had the whole window to run.
  adv_zotter_chocolates: {
    "2024-03": {
      1: 346,
      2: 494,
      3: 134,
      4: 529,
      5: 374,
      8: 336,
      9: 280,
      10: 112,
      12: 616,
    },
    "2024-04": { 1: 840, 2: 677, 3: 1087, 4: 642, 5: 371, 8: 1275, 11: 448 },
    "2024-05": {
      2: 445,
      3: 642,
      4: 450,
      5: 560,
      6: 560,
      7: 168,
      8: 168,
      9: 168,
      12: 188,
    },
    "2024-06": { 2: 148, 4: 168 },
    "2024-08": { 2: 336, 6: 168 },
    "2024-11": { 1: 168, 2: 1011, 11: 1102 },
    "2025-01": { 2: 280, 6: 313, 10: 583 },
    "2025-02": { 1: 1025, 12: 1041 },
    "2025-03": { 2: 1008, 5: 1244 },
    "2025-04": { 3: 313, 12: 907 },
    "2025-09": { 2: 261, 3: 196, 11: 196 },
    "2025-10": { 1: 259, 2: 130, 3: 518, 5: 171 },
    "2025-12": { 2: 130 },
    "2026-01": { 2: 194, 3: 194 },
    "2026-04": { 2: 194, 3: 324 },
    "2026-05": { 1: 130 },
  },
  // 1,567 reorders across all 31 cohorts — the densest block here, ahead of
  // Totally Bamboo's 1,136. Every cohort old enough to have the offsets fills
  // them, so this is the one brand where the curve is real end to end rather
  // than real in places and modeled in the gaps.
  adv_dairy_brand: {
    "2024-02": {
      2: 613,
      3: 149,
      4: 161,
      5: 299,
      6: 411,
      7: 370,
      8: 99,
      9: 674,
      11: 119,
    },
    "2024-03": {
      1: 789,
      2: 1799,
      3: 1093,
      4: 1029,
      5: 1107,
      6: 802,
      7: 1004,
      8: 1074,
      9: 669,
      10: 1099,
      11: 1453,
      12: 748,
    },
    "2024-04": {
      1: 934,
      2: 521,
      3: 1981,
      4: 603,
      5: 1064,
      6: 731,
      7: 1249,
      8: 985,
      9: 746,
      10: 926,
      11: 596,
      12: 791,
    },
    "2024-05": {
      1: 4237,
      2: 2984,
      3: 1770,
      4: 837,
      5: 1080,
      6: 1267,
      7: 458,
      8: 580,
      9: 366,
      10: 442,
      11: 863,
      12: 854,
    },
    "2024-06": {
      1: 1909,
      2: 1023,
      3: 1089,
      4: 1269,
      5: 1174,
      6: 1867,
      7: 1499,
      8: 478,
      9: 1410,
      10: 935,
      11: 1549,
      12: 1345,
    },
    "2024-07": {
      1: 2503,
      2: 1587,
      3: 3148,
      4: 3277,
      5: 2029,
      6: 1409,
      7: 524,
      8: 1456,
      9: 893,
      10: 964,
      11: 1206,
      12: 1158,
    },
    "2024-08": {
      1: 1466,
      2: 1249,
      3: 1382,
      4: 1895,
      5: 680,
      6: 464,
      7: 272,
      8: 267,
      9: 492,
      10: 394,
      11: 723,
      12: 371,
    },
    "2024-09": {
      1: 1353,
      2: 693,
      3: 912,
      4: 506,
      5: 233,
      6: 809,
      7: 437,
      8: 817,
      9: 514,
      10: 459,
      11: 1321,
      12: 726,
    },
    "2024-10": {
      1: 604,
      2: 611,
      3: 308,
      5: 226,
      6: 234,
      7: 154,
      8: 213,
      9: 153,
      10: 161,
      11: 104,
      12: 1274,
    },
    "2024-11": {
      1: 1095,
      2: 522,
      3: 258,
      4: 639,
      5: 495,
      6: 658,
      7: 511,
      8: 774,
      9: 85,
      10: 465,
      11: 419,
      12: 250,
    },
    "2024-12": {
      1: 343,
      2: 242,
      3: 490,
      4: 290,
      5: 361,
      6: 494,
      7: 238,
      8: 310,
      9: 286,
      11: 469,
      12: 317,
    },
    "2025-01": {
      1: 761,
      2: 709,
      3: 985,
      4: 1051,
      5: 566,
      6: 1132,
      8: 317,
      9: 861,
      10: 324,
      11: 825,
      12: 810,
    },
    "2025-02": {
      1: 282,
      2: 266,
      3: 203,
      4: 792,
      5: 506,
      6: 386,
      7: 372,
      8: 696,
      9: 227,
      10: 352,
      11: 400,
      12: 253,
    },
    "2025-03": {
      1: 265,
      2: 1097,
      3: 558,
      4: 1053,
      5: 262,
      6: 1156,
      7: 609,
      8: 558,
      9: 149,
      10: 305,
      11: 98,
      12: 426,
    },
    "2025-04": {
      1: 1015,
      2: 754,
      3: 1294,
      4: 1759,
      5: 648,
      6: 1536,
      7: 802,
      8: 297,
      9: 906,
      10: 328,
      11: 1042,
      12: 1607,
    },
    "2025-05": {
      1: 548,
      2: 1087,
      3: 1711,
      4: 1374,
      5: 377,
      6: 1802,
      7: 1104,
      8: 417,
      9: 407,
      10: 385,
      11: 371,
      12: 742,
    },
    "2025-06": {
      1: 1429,
      2: 387,
      3: 657,
      4: 646,
      5: 387,
      6: 698,
      7: 499,
      8: 807,
      9: 401,
      10: 746,
      11: 389,
      12: 205,
    },
    "2025-07": {
      1: 1183,
      2: 687,
      3: 322,
      4: 972,
      5: 392,
      6: 697,
      7: 1112,
      8: 125,
      9: 110,
      10: 617,
      11: 417,
      12: 1462,
    },
    "2025-08": {
      1: 3145,
      2: 1234,
      3: 2031,
      4: 204,
      5: 637,
      6: 486,
      7: 803,
      8: 553,
      9: 1188,
      10: 600,
      11: 565,
      12: 2562,
    },
    "2025-09": {
      1: 2579,
      2: 2383,
      3: 2703,
      4: 1972,
      5: 1495,
      6: 3013,
      7: 988,
      8: 2491,
      9: 2389,
      10: 1119,
      11: 1217,
      12: 491,
    },
    "2025-10": {
      1: 1525,
      2: 1610,
      3: 1084,
      4: 556,
      5: 1541,
      6: 71,
      7: 1235,
      8: 799,
      9: 676,
      10: 921,
      11: 120,
    },
    "2025-11": {
      1: 3337,
      2: 1431,
      3: 1087,
      4: 1729,
      5: 2061,
      6: 2015,
      7: 1749,
      8: 1844,
      9: 1532,
      10: 814,
    },
    "2025-12": { 1: 903, 2: 1159, 4: 586, 5: 185, 6: 373, 7: 250, 8: 947 },
    "2026-01": {
      1: 974,
      2: 1529,
      3: 1444,
      4: 1248,
      5: 1357,
      6: 1314,
      7: 1222,
      8: 958,
    },
    "2026-02": { 1: 487, 2: 435, 3: 458, 4: 778, 5: 563, 6: 224, 7: 335 },
    "2026-03": { 1: 813, 2: 1174, 3: 1428, 4: 2529, 5: 1355, 6: 345 },
    "2026-04": { 1: 1250, 2: 1089, 3: 1473, 4: 1112, 5: 1150 },
    "2026-05": { 1: 1810, 2: 2386, 3: 1482, 4: 473 },
    "2026-06": { 1: 2247, 2: 2500, 3: 790 },
    "2026-07": { 1: 2309, 2: 1602 },
    "2026-08": { 1: 631 },
  },
  // Do Good Candle Studio's entire reorder history, in one entry: a single $468
  // order in Sep 2024 from one of the two retailers acquired in Apr 2024, five
  // months after acquisition. It has no other cohort.
  adv_do_good_candle: {
    "2024-04": { 5: 468 },
  },
};

const CONFIGS: RealBrandConfig[] = [
  TOTALLY_BAMBOO,
  PENGUIN_RANDOM_HOUSE,
  GOOD_BOOK_COMPANY,
  MALWEST,
  KING_FLOYDS,
  OGGI,
  ELANA_GABRIELLE,
  KAARI_CO,
  WOODSY_CRAFT,
  ZOTTER_CHOCOLATES,
  DAIRY_BRAND,
  DO_GOOD_CANDLE,
];

const CONFIG_BY_TOKEN = new Map(
  CONFIGS.map((config) => [config.profileToken, config])
);

const MONTH_LABEL = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});

const IMPORT_META = {
  source: "snowflake" as const,
  importedAt: "2026-07-20T00:00:00.000Z",
  importedBy: "real-brand-data",
};

export const REAL_BRAND_PROFILE_TOKENS = CONFIGS.map(
  (config) => config.profileToken
);

export function isRealBrandProfile(token: string): boolean {
  return CONFIG_BY_TOKEN.has(token);
}

function monthLabel(isoMonth: string): string {
  const [year, month] = isoMonth.split("-").map(Number);
  return MONTH_LABEL.format(new Date(year, month - 1, 1));
}

function monthStart(isoMonth: string): string {
  return `${isoMonth}-01`;
}

function monthEnd(isoMonth: string): string {
  const [year, month] = isoMonth.split("-").map(Number);
  const day = new Date(year, month, 0).getDate();
  return `${isoMonth}-${String(day).padStart(2, "0")}`;
}

function buildPeriod(
  config: RealBrandConfig,
  entry: RealBrandMonth,
  scope: AdPerformanceScope
): FaireAdPerformancePeriodEntity {
  const reorder = entry.reorder;
  // Real brands carry actual per-month sales; legacy configs model reorder GMV
  // as `reorderConversions * new-customer AOV`.
  const accurate = entry.new.firstOrderSales != null;
  const reorderValue = reorder
    ? Math.round(reorder.conversions * config.aov)
    : 0;
  const segFirstOrder = (seg?: SegmentMonth) => seg?.firstOrderSales ?? 0;
  const segSalesToDate = (seg?: SegmentMonth) =>
    seg?.salesToDate ?? seg?.firstOrderSales ?? 0;

  let spend: number;
  let sales: number;
  let impressions: number;
  let clicks: number;
  let budget: number;
  let newCustomersAcquired: number;
  let reorderConversions: number;
  let firstOrderSalesDollars: number | undefined;
  let salesToDateDollars: number | undefined;

  if (scope === "NEW_MARKETPLACE") {
    spend = entry.new.spend;
    sales = entry.new.firstYearValue;
    impressions = entry.new.impressions;
    clicks = entry.new.clicks;
    budget = entry.new.budget;
    newCustomersAcquired = entry.new.conversions;
    reorderConversions = 0;
    if (accurate) {
      firstOrderSalesDollars = segFirstOrder(entry.new);
      salesToDateDollars = segSalesToDate(entry.new);
    }
  } else if (scope === "REORDER_MARKETPLACE") {
    spend = reorder?.spend ?? 0;
    sales = accurate ? (reorder?.firstYearValue ?? 0) : reorderValue;
    impressions = reorder?.impressions ?? 0;
    clicks = reorder?.clicks ?? 0;
    budget = reorder?.budget ?? 0;
    newCustomersAcquired = 0;
    reorderConversions = reorder?.conversions ?? 0;
    if (accurate) {
      firstOrderSalesDollars = segFirstOrder(reorder);
      salesToDateDollars = segSalesToDate(reorder);
    }
  } else {
    spend = entry.new.spend + (reorder?.spend ?? 0);
    sales =
      entry.new.firstYearValue +
      (accurate ? (reorder?.firstYearValue ?? 0) : reorderValue);
    impressions = entry.new.impressions + (reorder?.impressions ?? 0);
    clicks = entry.new.clicks + (reorder?.clicks ?? 0);
    budget = entry.pooledBudget ?? entry.new.budget + (reorder?.budget ?? 0);
    newCustomersAcquired = entry.new.conversions;
    reorderConversions = reorder?.conversions ?? 0;
    if (accurate) {
      firstOrderSalesDollars =
        segFirstOrder(entry.new) + segFirstOrder(reorder);
      salesToDateDollars = segSalesToDate(entry.new) + segSalesToDate(reorder);
    }
  }

  const scopeToken =
    scope === "NEW_MARKETPLACE"
      ? "new"
      : scope === "REORDER_MARKETPLACE"
        ? "reorder"
        : "ovw";

  return {
    token: `perf_${config.tokenPrefix}_${scopeToken}_${entry.month.replace("-", "")}`,
    type: "adPerformancePeriod",
    scope,
    periodType: "RECEIPT",
    startDate: monthStart(entry.month),
    endDate: monthEnd(entry.month),
    impressions,
    clicks,
    adsSpendDollars: spend,
    budgetDollars: budget,
    salesDollars: sales,
    firstOrderSalesDollars,
    salesToDateDollars,
    bestEstimateRoas: spend > 0 ? Number((sales / spend).toFixed(2)) : 0,
    bestEstimateType: "PROJECTED",
    budgetUtilization:
      entry.projectedUtilization ??
      (budget > 0 ? Math.min(0.99, spend / budget) : undefined),
    newCustomersAcquired,
    reorderConversions,
    meta: IMPORT_META,
  };
}

export function getRealBrandPeriods(
  profileToken: string,
  scope: AdPerformanceScope
): FaireAdPerformancePeriodEntity[] {
  const config = CONFIG_BY_TOKEN.get(profileToken);
  if (!config) {
    return [];
  }
  if (scope === "REORDER_MARKETPLACE") {
    return config.months
      .filter((entry) => entry.reorder)
      .map((entry) => buildPeriod(config, entry, scope));
  }
  return config.months.map((entry) => buildPeriod(config, entry, scope));
}

/** Partial in-progress month as an ACTIVE overview period (null for paused advertisers). */
export function getRealBrandCurrentPeriod(
  profileToken: string
): FaireAdPerformancePeriodEntity | null {
  const config = CONFIG_BY_TOKEN.get(profileToken);
  if (!config?.current) {
    return null;
  }
  const period = buildPeriod(config, config.current, "OVERVIEW");
  return { ...period, periodType: "ACTIVE" };
}

export function getRealBrandChartMonthLabels(profileToken: string): string[] {
  const config = CONFIG_BY_TOKEN.get(profileToken);
  if (!config) {
    return [];
  }
  return config.months.slice(-12).map((entry) => monthLabel(entry.month));
}

export function getRealBrandActivityMonthLabels(
  profileToken: string
): string[] {
  const config = CONFIG_BY_TOKEN.get(profileToken);
  if (!config) {
    return [];
  }
  return config.months.map((entry) => monthLabel(entry.month));
}

export function getRealBrandAnchorLabel(profileToken: string): string {
  return CONFIG_BY_TOKEN.get(profileToken)?.anchorLabel ?? "";
}

/**
 * Real continued-sales dollars by months-since-acquisition for one cohort, or
 * undefined when no reorder has been attributed to it. `isoMonth` is the cohort's
 * acquisition month, e.g. "2026-01".
 */
export function getRealCohortContinuedSales(
  profileToken: string,
  isoMonth: string
): Record<number, number> | undefined {
  return CONTINUED_SALES_BY_COHORT[profileToken]?.[isoMonth];
}
